import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  addModuleSystem,
  removeModuleSystem,
} from "../../../store/features/moduleSlice";
import { OPTIONAL_SYSTEM_TYPES } from "../../../constants/optionalSystems";
import { calculateModuleCost } from "../../../utils/validation/moduleValidation";
import { PROTECTABLE_SHIP_SYSTEMS } from "../../../constants/protectableShipSystems";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { formatCurrency, formatNumber } from "../../../utils/formatting";
import TonnageInput from "../../common/TonnageInput";

/**
 * Protected Systems Selector Modal for Armoured Bulkheads
 */
const ProtectedSystemsSelector: React.FC<{
  onConfirm: (protectedIds: string[], protectedTonnage: number) => void;
  onCancel: () => void;
  moduleConfig: any;
}> = ({ onConfirm, onCancel, moduleConfig }) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [showError, setShowError] = useState(false);

  const BULKHEAD_COST_PER_TON = 0.2 * 1_000_000; // Cr200,000 per ton
  const BULKHEAD_TONNAGE_PERCENT = 0.1; // 10%

  // Get all installed systems from the module
  const installedSystems = useAppSelector((state) => state.module.moduleConfig?.systems || {}) as any;
  
  // Create eligible systems list for modules - show all protectable systems that exist in the module
  const eligibleSystems = PROTECTABLE_SHIP_SYSTEMS.filter(sys => {
    // Check if the system exists in the module configuration
    switch (sys.id) {
      case "bridge":
        return installedSystems.bridge;
      case "computer":
        return installedSystems.computer && installedSystems.computer.length > 0;
      case "sensors":
        return installedSystems.sensors;
      case "powerPlant":
        return installedSystems.powerPlant && installedSystems.powerPlant.length > 0;
      case "maneuverDrive":
        return installedSystems.drives && installedSystems.drives.some((drive: any) => drive.type === "maneuver");
      case "jumpDrive":
        return installedSystems.drives && installedSystems.drives.some((drive: any) => drive.type === "jump");
      case "reactionDrive":
        return installedSystems.drives && installedSystems.drives.some((drive: any) => drive.type === "reaction");
      case "fuelTanks":
        return installedSystems.fuel;
      case "staterooms":
        return installedSystems.staterooms && installedSystems.staterooms.length > 0;
      case "cargo":
        return installedSystems.cargo;
      case "weapons":
        return installedSystems.weaponMounts && installedSystems.weaponMounts.length > 0;
      case "hangar":
        return installedSystems.optionalSystems && installedSystems.optionalSystems.some((sys: any) => sys.subtype === "hangar");
      case "lifeSupport":
        return installedSystems.optionalSystems && installedSystems.optionalSystems.some((sys: any) => sys.subtype === "lifeSupport");
      case "medicalBay":
        return installedSystems.optionalSystems && installedSystems.optionalSystems.some((sys: any) => sys.subtype === "medicalBay");
      case "droneBay":
        return installedSystems.optionalSystems && installedSystems.optionalSystems.some((sys: any) => sys.subtype === "droneBay");
      case "externalSystems":
        return installedSystems.optionalSystems && installedSystems.optionalSystems.some((sys: any) => sys.type === "external");
      case "internalSystems":
        return installedSystems.optionalSystems && installedSystems.optionalSystems.some((sys: any) => sys.type === "internal");
      case "modularHull":
        return installedSystems.optionalSystems && installedSystems.optionalSystems.some((sys: any) => sys.subtype === "modularHull");
      default:
        return false;
    }
  });

  const getSystemTonnage = (id: string): number => {
    // Calculate tonnage for each system type
    switch (id) {
      case "bridge":
        return installedSystems.bridge?.tonnage || 0;
      case "computer":
        return (installedSystems.computer || []).reduce((sum: number, comp: any) => sum + (comp.tonnage || 0), 0);
      case "sensors":
        return installedSystems.sensors?.tons || 0;
      case "powerPlant":
        return (installedSystems.powerPlant || []).reduce((sum: number, plant: any) => sum + (plant.tonnage || 0), 0);
      case "maneuverDrive":
        return (installedSystems.drives || []).filter((drive: any) => drive.type === "maneuver").reduce((sum: number, drive: any) => sum + (drive.tonnage || 0), 0);
      case "jumpDrive":
        return (installedSystems.drives || []).filter((drive: any) => drive.type === "jump").reduce((sum: number, drive: any) => sum + (drive.tonnage || 0), 0);
      case "reactionDrive":
        return (installedSystems.drives || []).filter((drive: any) => drive.type === "reaction").reduce((sum: number, drive: any) => sum + (drive.tonnage || 0), 0);
      case "fuelTanks":
        return installedSystems.fuel?.tonnage || 0;
      case "staterooms":
        return (installedSystems.staterooms || []).reduce((sum: number, room: any) => sum + (room.tonnage || 0), 0);
      case "cargo":
        return installedSystems.cargo?.tonnage || 0;
      case "weapons":
        return (installedSystems.weaponMounts || []).reduce((sum: number, mount: any) => sum + (mount.tons || 0), 0);
      case "hangar":
        return (installedSystems.optionalSystems || []).filter((sys: any) => sys.subtype === "hangar").reduce((sum: number, sys: any) => sum + (sys.tonnage || 0), 0);
      case "lifeSupport":
        return (installedSystems.optionalSystems || []).filter((sys: any) => sys.subtype === "lifeSupport").reduce((sum: number, sys: any) => sum + (sys.tonnage || 0), 0);
      case "medicalBay":
        return (installedSystems.optionalSystems || []).filter((sys: any) => sys.subtype === "medicalBay").reduce((sum: number, sys: any) => sum + (sys.tonnage || 0), 0);
      case "droneBay":
        return (installedSystems.optionalSystems || []).filter((sys: any) => sys.subtype === "droneBay").reduce((sum: number, sys: any) => sum + (sys.tonnage || 0), 0);
      case "externalSystems":
        return (installedSystems.optionalSystems || []).filter((sys: any) => sys.type === "external").reduce((sum: number, sys: any) => sum + (sys.tonnage || 0), 0);
      case "internalSystems":
        return (installedSystems.optionalSystems || []).filter((sys: any) => sys.type === "internal").reduce((sum: number, sys: any) => sum + (sys.tonnage || 0), 0);
      case "modularHull":
        return (installedSystems.optionalSystems || []).filter((sys: any) => sys.subtype === "modularHull").reduce((sum: number, sys: any) => sum + (sys.tonnage || 0), 0);
      default:
        return 0;
    }
  };

  const protectedTonnage = selected.reduce((sum, id) => sum + getSystemTonnage(id), 0);
  const cost = protectedTonnage * BULKHEAD_COST_PER_TON;
  const bulkheadTonnage = protectedTonnage * BULKHEAD_TONNAGE_PERCENT;

  const handleToggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
    setShowError(false);
  };

  const handleConfirm = () => {
    if (selected.length === 0) {
      setShowError(true);
      return;
    }
    onConfirm(selected, protectedTonnage);
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Armoured Bulkhead Configuration</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Select which systems you want to protect with armoured bulkheads. 
          The bulkhead will consume 10% of the protected tonnage and cost Cr200,000 per ton protected.
        </p>
        
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900 dark:text-gray-100">Available Systems to Protect:</h4>
          {eligibleSystems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {eligibleSystems.map((sys) => (
                <label
                  key={sys.id}
                  className="flex items-center space-x-2 cursor-pointer p-2 border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(sys.id)}
                    onChange={() => handleToggle(sys.id)}
                    className="form-checkbox h-4 w-4 text-blue-600"
                  />
                  <span className="text-gray-900 dark:text-gray-100">
                    {sys.name}{" "}
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      ({getSystemTonnage(sys.id)} tons)
                    </span>
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <div className="text-gray-500 dark:text-gray-400 text-sm">
              No systems available to protect. Add some systems to your module first.
            </div>
          )}
          
          {showError && (
            <div className="text-red-600 text-sm">
              Select at least one system to protect.
            </div>
          )}
          
          {/* Live summary of selected systems and totals */}
          {selected.length > 0 && (
            <div className="p-3 bg-blue-50 rounded text-sm">
              <b>Summary:</b>{" "}
              Protecting <b>{selected.length}</b> system{selected.length > 1 ? "s" : ""}:{" "}
              {selected
                .map((id) => {
                  const sys = eligibleSystems.find((s) => s.id === id);
                  return sys ? sys.name : id;
                })
                .join(", ")}
              <br />
              Total Protected Tonnage: <b>{protectedTonnage}</b> tons | Bulkhead
              Tonnage: <b>{bulkheadTonnage.toFixed(1)}</b> tons | Cost:{" "}
              <b>{formatCurrency(cost)}</b>
            </div>
          )}
          
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleConfirm}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Confirm Protection
            </button>
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Individual System Card Component
 */
const SystemCard: React.FC<{ system: any; moduleConfig: any }> = ({ system, moduleConfig }) => {
  const dispatch = useAppDispatch();
  const installedSystems = useAppSelector((state) => state.module.moduleConfig?.systems.optionalSystems || []);
  
  const [showProtectedSelector, setShowProtectedSelector] = useState(false);
  const [quantity, setQuantity] = useState(1);
  
  const isInstalled = installedSystems.some(s => s.id === system.id);
  const isOverTechLevel = system.techLevel > moduleConfig.techLevel;
  const totalTons = installedSystems.reduce((sum, sys) => sum + (sys.tonnage || 0), 0);
  
  // Calculate dynamic stats based on system properties
  let calculatedCost = system.cost;
  let calculatedTonnage = system.tonnage;
  let calculatedPower = system.power;
  
  // Handle systems with costPerTon
  if (system.properties?.costPerTon) {
    calculatedCost = calculatedTonnage * system.properties.costPerTon;
  }
  
  // Handle systems with quantity
  if (system.properties?.quantity || quantity > 1) {
    const qty = system.properties?.quantity || quantity;
    calculatedCost = calculatedCost * qty;
    calculatedTonnage = calculatedTonnage * qty;
    calculatedPower = calculatedPower * qty;
  }
  
  const wouldExceedTonnage = (totalTons + calculatedTonnage) > moduleConfig.size;
  const isDisabled = isInstalled || isOverTechLevel || wouldExceedTonnage;

  const handleAddSystem = () => {
    // Special handling for Armoured Bulkhead
    if (system.subtype === "armouredBulkhead") {
      setShowProtectedSelector(true);
      return;
    }
    
    // Regular system addition with quantity
    const finalSystem = {
      ...system,
      cost: calculatedCost,
      tonnage: calculatedTonnage,
      power: calculatedPower,
      properties: {
        ...system.properties,
        quantity: quantity,
      },
      id: `${system.id}_${Date.now()}`, // Ensure unique ID
    };
    
    dispatch(addModuleSystem({
      systemType: "optionalSystems",
      system: finalSystem,
    }));
  };

  const handleRemoveSystem = () => {
    dispatch(removeModuleSystem({
      systemType: "optionalSystems",
      systemId: system.id,
    }));
  };

  const handleConfirmProtection = (protectedIds: string[], protectedTonnage: number) => {
    // Calculate armoured bulkhead stats based on protected tonnage
    const bulkheadTonnage = protectedTonnage * 0.1; // 10% of protected tonnage
    const cost = protectedTonnage * 200000; // Cr200,000 per ton protected
    
    const finalSystem = {
      ...system,
      protectedSystems: protectedIds,
      protectedTonnage,
      tonnage: bulkheadTonnage,
      cost: cost,
      properties: {
        ...system.properties,
        calculated: true,
      },
      id: `${system.id}_${Date.now()}`, // Ensure unique ID
    };
    
    dispatch(addModuleSystem({
      systemType: "optionalSystems",
      system: finalSystem,
    }));
    
    setShowProtectedSelector(false);
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString();
  };

  return (
    <>
      <div
        className={`border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between ${
          isDisabled ? "bg-gray-100 dark:bg-gray-800 opacity-60" : "bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
        }`}
      >
        <div>
          <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{system.name}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
            <div>Cost: {formatCurrency(calculatedCost)} Cr</div>
            <div>Tons: {calculatedTonnage.toFixed(1)}</div>
            <div>Power: {calculatedPower}</div>
            <div>TL: {system.techLevel}</div>
            <div>Type: {system.type}</div>
            {system.subtype === "armouredBulkhead" && (
              <div className="text-blue-600 dark:text-blue-400 font-medium">⚠️ Requires configuration</div>
            )}
          </div>
          
          {/* Quantity input for systems that support it */}
          {(system.properties?.costPerTon || system.properties?.quantity || system.properties?.tonsPerTraveller) && (
            <div className="mb-2">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Quantity
              </label>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                disabled={isInstalled}
              />
            </div>
          )}
        </div>
        {isInstalled ? (
          <button
            onClick={handleRemoveSystem}
            className="mt-3 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-medium transition-colors"
          >
            Remove
          </button>
        ) : (
          <button
            onClick={handleAddSystem}
            disabled={isDisabled}
            className={`mt-3 px-3 py-1 rounded text-sm font-medium transition-colors ${
              isDisabled
                ? "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
            title={
              isInstalled
                ? "Already installed"
                : isOverTechLevel
                ? `Requires TL ${system.techLevel}`
                : wouldExceedTonnage
                ? "Would exceed module tonnage"
                : system.subtype === "armouredBulkhead"
                ? "Configure protection settings"
                : "Add system"
            }
          >
            {system.subtype === "armouredBulkhead" ? "Configure" : "Add"}
          </button>
        )}
      </div>
      
      {/* Protected Systems Selector Modal */}
      {showProtectedSelector && (
        <ProtectedSystemsSelector
          onConfirm={handleConfirmProtection}
          onCancel={() => setShowProtectedSelector(false)}
          moduleConfig={moduleConfig}
        />
      )}
    </>
  );
};

// StructureSystemsWrapper removed - structure systems not allowed in modular hulls

/**
 * Wrapper component for Power Systems
 */
const PowerSystemsWrapper: React.FC<{ moduleConfig: any }> = ({ moduleConfig }) => {
  const dispatch = useAppDispatch();
  
  // Solar Panel IDs for dropdown grouping
  const SOLAR_PANEL_IDS = [
    "solarPanelsBasic",
    "solarPanelsImproved", 
    "solarPanelsEnhanced",
    "solarPanelsAdvanced",
  ];
  
  // State for solar panel selection
  const [selectedSolarPanel, setSelectedSolarPanel] = useState(SOLAR_PANEL_IDS[0]);
  const [solarPanelTonnage, setSolarPanelTonnage] = useState(1);
  
  const allSystems = OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "power");
  
  // Separate solar panels from other power systems
  const solarPanelOptions = allSystems.filter(system => SOLAR_PANEL_IDS.includes(system.id));
  const otherPowerSystems = allSystems.filter(system => !SOLAR_PANEL_IDS.includes(system.id));
  
  const selectedSolarPanelObj = solarPanelOptions.find(s => s.id === selectedSolarPanel);
  
  const handleAddSolarPanel = () => {
    if (!selectedSolarPanelObj) return;
    
    // Calculate solar panel stats based on tonnage and tech level
    const techLevel = selectedSolarPanelObj.techLevel;
    const costPerUnit = (selectedSolarPanelObj.properties?.costPerUnit as any)?.[techLevel] || 100000;
    const powerPerUnit = (selectedSolarPanelObj.properties?.powerPerUnit as any)?.[techLevel] || 0.25;
    
    const finalSystem = {
      ...selectedSolarPanelObj,
      tonnage: solarPanelTonnage,
      cost: costPerUnit * solarPanelTonnage,
      power: powerPerUnit * solarPanelTonnage,
      id: `${selectedSolarPanelObj.id}_${Date.now()}`, // Ensure unique ID
    };
    
    dispatch(addModuleSystem({
      systemType: "optionalSystems",
      system: finalSystem,
    }));
  };
  
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString();
  };
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Power Systems</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Solar Panels Card with Dropdown */}
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700">
          <div>
            <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Solar Panels
            </div>
            <select
              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm mb-2 dark:bg-gray-600 dark:text-gray-100"
              value={selectedSolarPanel}
              onChange={(e) => setSelectedSolarPanel(e.target.value)}
            >
              {solarPanelOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </select>
            <div className="mb-2">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tonnage
              </label>
              <input
                type="number"
                min="1"
                value={solarPanelTonnage}
                onChange={(e) => setSolarPanelTonnage(Number(e.target.value))}
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
              />
            </div>

            {selectedSolarPanelObj && (
              <ul className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <li>
                  <b>Cost:</b> {formatCurrency(((selectedSolarPanelObj.properties?.costPerUnit as any)?.[selectedSolarPanelObj.techLevel] || 100000) * solarPanelTonnage)} Cr
                </li>
                <li>
                  <b>Tonnage:</b> {solarPanelTonnage} tons
                </li>
                <li>
                  <b>Power:</b> {(((selectedSolarPanelObj.properties?.powerPerUnit as any)?.[selectedSolarPanelObj.techLevel] || 0.25) * solarPanelTonnage).toFixed(1)} Power
                </li>
              </ul>
            )}
          </div>

          <div className="mt-2 flex gap-2">
            <button
              className="w-full mt-2 px-3 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
              onClick={handleAddSolarPanel}
            >
              Add
            </button>
          </div>
        </div>

        {/* Other power systems */}
        {otherPowerSystems.map((system) => (
          <SystemCard key={system.id} system={system} moduleConfig={moduleConfig} />
        ))}
      </div>
    </div>
  );
};

/**
 * Wrapper component for Drive Systems
 */
const DriveSystemsWrapper: React.FC<{ moduleConfig: any }> = ({ moduleConfig }) => {
  const dispatch = useAppDispatch();
  const installedSystems = useAppSelector((state) => state.module.moduleConfig?.systems.optionalSystems || []);
  
  // State for High-Burn Thruster thrust selection
  const [thrusterThrust, setThrusterThrust] = useState<number>(1);
  
  const allSystems = OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "drive");
  
  // Separate high-burn thruster from other drive systems
  const highBurnSystems = allSystems.filter(system => system.subtype === "highBurn");
  const otherDriveSystems = allSystems.filter(system => system.subtype !== "highBurn");
  
  const handleAddHighBurnThruster = (system: any) => {
    // Calculate high-burn thruster stats based on thrust rating
    const thrustCost = thrusterThrust * 50000; // Cr50,000 per thrust point
    const thrustTonnage = thrusterThrust * 0.5; // 0.5 tons per thrust point
    const thrustPower = thrusterThrust * 2; // 2 power per thrust point
    
    const finalSystem = {
      ...system,
      cost: thrustCost,
      tonnage: thrustTonnage,
      power: thrustPower,
      properties: {
        ...system.properties,
        selectedThrust: thrusterThrust,
      },
      id: `${system.id}_${Date.now()}`, // Ensure unique ID
    };
    
    dispatch(addModuleSystem({
      systemType: "optionalSystems",
      system: finalSystem,
    }));
  };
  
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString();
  };
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Drive Systems</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* High-Burn Thruster with thrust selection */}
        {highBurnSystems.map((system) => {
          const isInstalled = installedSystems.some(s => s.id === system.id);
          const isOverTechLevel = system.techLevel > moduleConfig.techLevel;
          const totalTons = installedSystems.reduce((sum, sys) => sum + (sys.tonnage || 0), 0);
          const calculatedCost = thrusterThrust * 50000;
          const calculatedTonnage = thrusterThrust * 0.5;
          const calculatedPower = thrusterThrust * 2;
          const wouldExceedTonnage = (totalTons + calculatedTonnage) > moduleConfig.size;
          const isDisabled = isInstalled || isOverTechLevel || wouldExceedTonnage;
          
          return (
            <div
              key={system.id}
              className={`border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between ${
                isDisabled ? "bg-gray-100 dark:bg-gray-800 opacity-60" : "bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
              }`}
            >
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{system.name}</div>

                {/* Thrust Rating Selection */}
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Thrust Rating
                  </label>
                  <select
                    value={thrusterThrust}
                    onChange={(e) => setThrusterThrust(Number(e.target.value))}
                    className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                    disabled={isInstalled}
                  >
                    {[1, 2, 3, 4, 5, 6].map((val) => (
                      <option key={val} value={val}>
                        Thrust {val}
                      </option>
                    ))}
                  </select>
                </div>

                <ul className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <li>
                    <b>Cost:</b> {formatCurrency(calculatedCost)} Cr
                  </li>
                  <li>
                    <b>Tonnage:</b> {calculatedTonnage.toFixed(1)} tons
                  </li>
                  <li>
                    <b>Power:</b> {calculatedPower} Power
                  </li>
                  <li>
                    <b>No Inertial Compensation</b>
                  </li>
                  <li>
                    <b>Cumulative with main drives</b>
                  </li>
                </ul>
              </div>

              <div className="mt-2 flex gap-2">
                {!isInstalled ? (
                  <button
                    className={`w-full mt-2 px-3 py-1 rounded text-white font-semibold ${
                      isDisabled
                        ? "bg-gray-400 dark:bg-gray-600 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                    onClick={() => handleAddHighBurnThruster(system)}
                    disabled={isDisabled}
                    title={
                      isInstalled
                        ? "Already installed"
                        : isOverTechLevel
                        ? `Requires TL ${system.techLevel}`
                        : wouldExceedTonnage
                        ? "Would exceed module tonnage"
                        : "Add system"
                    }
                  >
                    Add
                  </button>
                ) : (
                  <button
                    className="px-4 py-1 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                    onClick={() => {
                      dispatch(removeModuleSystem({
                        systemType: "optionalSystems",
                        systemId: system.id,
                      }));
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {/* Other drive systems */}
        {otherDriveSystems.map((system) => (
          <SystemCard key={system.id} system={system} moduleConfig={moduleConfig} />
        ))}
      </div>
    </div>
  );
};

/**
 * Wrapper component for Fuel Systems
 */
const FuelSystemsWrapper: React.FC<{ moduleConfig: any }> = ({ moduleConfig }) => {
  const dispatch = useAppDispatch();
  const installedSystems = useAppSelector((state) => state.module.moduleConfig?.systems.optionalSystems || []);
  
  // State for per-card inputs
  const [cardInputs, setCardInputs] = useState<{ [subtype: string]: any }>({});
  const [cardErrors, setCardErrors] = useState<{ [subtype: string]: string | null }>({});
  
  const allSystems = OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "fuel");
  
  const handleAddFuelSystem = (system: any) => {
    const inputValue = cardInputs[system.subtype] || 1;
    const error = cardErrors[system.subtype];
    
    if (error) return;
    
    let finalSystem = { ...system };
    let calculatedCost = system.cost;
    let calculatedTonnage = system.tonnage;
    let calculatedPower = system.power;
    
    // Calculate stats based on system type
    switch (system.subtype) {
      case "collapsibleTank":
        calculatedTonnage = inputValue;
        calculatedCost = inputValue * 100000; // Cr100,000 per ton
        calculatedPower = 0;
        break;
      case "dropTank":
        calculatedTonnage = inputValue;
        calculatedCost = inputValue * 50000; // Cr50,000 per ton
        calculatedPower = 0;
        break;
      case "fuelCargoContainer":
        calculatedTonnage = inputValue * 1.05; // 5% additional for equipment
        calculatedCost = inputValue * 20000; // Cr20,000 per ton
        calculatedPower = 0;
        break;
      case "fuelProcessor":
        calculatedTonnage = inputValue;
        calculatedCost = inputValue * 500000; // Cr500,000 per ton
        calculatedPower = inputValue; // 1 power per ton
        break;
      case "fuelTankCompartment":
        calculatedTonnage = inputValue;
        calculatedCost = inputValue * 4000; // Cr4,000 per ton
        calculatedPower = 0;
        break;
      case "mountableTank":
        calculatedTonnage = inputValue;
        calculatedCost = inputValue * 1000; // Cr1,000 per ton
        calculatedPower = 0;
        break;
      case "ramscoop":
        const baseTonnage = Math.max(10, Math.ceil(moduleConfig.size * 0.01 + 5));
        calculatedTonnage = baseTonnage + (inputValue || 0);
        calculatedCost = calculatedTonnage * 100000; // Cr100,000 per ton
        calculatedPower = 0;
        break;
      case "metalHydride":
        // Metal Hydride replaces all fuel tankage - cost calculated differently
        calculatedTonnage = 0; // No additional tonnage
        calculatedCost = moduleConfig.size * 200000; // Cr200,000 per ton of module
        calculatedPower = 0;
        break;
    }
    
    finalSystem = {
      ...system,
      cost: calculatedCost,
      tonnage: calculatedTonnage,
      power: calculatedPower,
      properties: {
        ...system.properties,
        inputValue: inputValue,
        allocation: system.subtype === "fuelCargoContainer" ? (cardInputs[`${system.subtype}_allocation`] || "fuel") : undefined,
      },
      id: `${system.id}_${Date.now()}`, // Ensure unique ID
    };
    
    dispatch(addModuleSystem({
      systemType: "optionalSystems",
      system: finalSystem,
    }));
  };
  
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString();
  };
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Fuel Systems</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {allSystems.map((system) => {
          const isInstalled = installedSystems.some(s => s.id === system.id);
          const isOverTechLevel = system.techLevel > moduleConfig.techLevel;
          const totalTons = installedSystems.reduce((sum, sys) => sum + (sys.tonnage || 0), 0);
          const inputValue = cardInputs[system.subtype] || 1;
          const error = cardErrors[system.subtype];
          
          // Calculate display values
          let displayCost = system.cost;
          let displayTonnage = system.tonnage;
          let displayPower = system.power;
          
          switch (system.subtype) {
            case "collapsibleTank":
              displayTonnage = inputValue;
              displayCost = inputValue * 100000;
              break;
            case "dropTank":
              displayTonnage = inputValue;
              displayCost = inputValue * 50000;
              break;
            case "fuelCargoContainer":
              displayTonnage = inputValue * 1.05;
              displayCost = inputValue * 20000;
              break;
            case "fuelProcessor":
              displayTonnage = inputValue;
              displayCost = inputValue * 500000;
              displayPower = inputValue;
              break;
            case "fuelTankCompartment":
              displayTonnage = inputValue;
              displayCost = inputValue * 4000;
              break;
            case "mountableTank":
              displayTonnage = inputValue;
              displayCost = inputValue * 1000;
              break;
            case "ramscoop":
              const baseTonnage = Math.max(10, Math.ceil(moduleConfig.size * 0.01 + 5));
              displayTonnage = baseTonnage + (inputValue || 0);
              displayCost = displayTonnage * 100000;
              break;
            case "metalHydride":
              displayCost = moduleConfig.size * 200000;
              break;
          }
          
          const wouldExceedTonnage = (totalTons + displayTonnage) > moduleConfig.size;
          const isDisabled = isInstalled || isOverTechLevel || wouldExceedTonnage || !!error;
          
          return (
            <div
              key={system.id}
              className={`border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between ${
                isDisabled ? "bg-gray-100 dark:bg-gray-800 opacity-60" : "bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
              }`}
            >
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{system.name}</div>
                
                <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <div>Cost: {formatCurrency(displayCost)} Cr</div>
                  <div>Tonnage: {displayTonnage.toFixed(1)} tons</div>
                  <div>Power: {displayPower} Power</div>
                  <div>TL: {system.techLevel}</div>
                </div>
                
                {/* System-specific input fields */}
                {system.subtype === "collapsibleTank" && (
                  <div>
                    <TonnageInput
                      value={inputValue}
                      onChange={(value) => {
                        setCardInputs((inputs) => ({
                          ...inputs,
                          [system.subtype]: value,
                        }));
                        setCardErrors((errors) => ({
                          ...errors,
                          [system.subtype]: value < 1 ? "Tonnage must be at least 1" : null,
                        }));
                      }}
                      systemType="fuelTank"
                      category="fuelSystems"
                      label="Full Tank Tonnage"
                      placeholder="Enter tonnage"
                      className="text-xs"
                      showValidation={true}
                      disabled={isInstalled}
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Empty tank uses 1% of full tonnage.
                    </span>
                  </div>
                )}
                
                {system.subtype === "dropTank" && (
                  <div>
                    <TonnageInput
                      value={inputValue}
                      onChange={(value) => {
                        setCardInputs((inputs) => ({
                          ...inputs,
                          [system.subtype]: value,
                        }));
                        setCardErrors((errors) => ({
                          ...errors,
                          [system.subtype]: value < 1 ? "Tonnage must be at least 1" : null,
                        }));
                      }}
                      systemType="fuelTank"
                      category="fuelSystems"
                      label="Drop Tank Tonnage"
                      placeholder="Enter tonnage"
                      className="text-xs"
                      showValidation={true}
                      disabled={isInstalled}
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Mount uses 0.4% of tank tonnage.
                    </span>
                  </div>
                )}
                
                {system.subtype === "fuelCargoContainer" && (
                  <div>
                    <TonnageInput
                      value={inputValue}
                      onChange={(value) => {
                        setCardInputs((inputs) => ({
                          ...inputs,
                          [system.subtype]: value,
                        }));
                        setCardErrors((errors) => ({
                          ...errors,
                          [system.subtype]: value < 1 ? "Capacity must be at least 1" : null,
                        }));
                      }}
                      systemType="fuelCapacity"
                      category="fuelSystems"
                      label="Container Capacity"
                      placeholder="Enter capacity"
                      className="text-xs"
                      showValidation={true}
                      disabled={isInstalled}
                    />
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mt-2 mb-1">
                      Configure As
                    </label>
                    <select
                      value={cardInputs[`${system.subtype}_allocation`] || "fuel"}
                      onChange={(e) =>
                        setCardInputs((inputs) => ({
                          ...inputs,
                          [`${system.subtype}_allocation`]: e.target.value,
                        }))
                      }
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                      disabled={isInstalled}
                    >
                      <option value="fuel">Fuel</option>
                      <option value="cargo">Cargo</option>
                    </select>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Additional 5% tonnage for equipment.
                    </span>
                  </div>
                )}
                
                {system.subtype === "fuelProcessor" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Processor Tonnage
                    </label>
                    <input
                      type="number"
                      value={inputValue}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setCardInputs((inputs) => ({
                          ...inputs,
                          [system.subtype]: value,
                        }));
                        setCardErrors((errors) => ({
                          ...errors,
                          [system.subtype]: value < 1 ? "Tonnage must be at least 1" : null,
                        }));
                      }}
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                      min="1"
                      step="0.1"
                      disabled={isInstalled}
                    />
                  </div>
                )}
                
                {system.subtype === "fuelScoop" && (
                  <div>
                  </div>
                )}
                
                {system.subtype === "fuelTankCompartment" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Compartment Tonnage
                    </label>
                    <input
                      type="number"
                      value={inputValue}
                      onChange={(e) => {
                        const value = Math.max(1, Number(e.target.value));
                        setCardInputs((inputs) => ({
                          ...inputs,
                          [system.subtype]: value,
                        }));
                        setCardErrors((errors) => ({
                          ...errors,
                          [system.subtype]: value < 1 ? "Tonnage must be at least 1" : null,
                        }));
                      }}
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                      min="1"
                      step="0.1"
                      disabled={isInstalled}
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Cr4,000 per ton. This will use {inputValue} tons of your fuel tankage to add {inputValue} tons of cargo space.
                    </span>
                  </div>
                )}
                
                {system.subtype === "metalHydride" && (
                  <div>
                  </div>
                )}
                
                {system.subtype === "mountableTank" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tank Tonnage
                    </label>
                    <input
                      type="number"
                      value={inputValue}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setCardInputs((inputs) => ({
                          ...inputs,
                          [system.subtype]: value,
                        }));
                        setCardErrors((errors) => ({
                          ...errors,
                          [system.subtype]: value < 1 ? "Tonnage must be at least 1" : null,
                        }));
                      }}
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                      min="1"
                      step="0.1"
                      disabled={isInstalled}
                    />
                  </div>
                )}
                
                {system.subtype === "ramscoop" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Additional Ramscoop Tonnage
                    </label>
                    <input
                      type="number"
                      value={inputValue}
                      onChange={(e) => {
                        const value = Math.max(0, Number(e.target.value));
                        setCardInputs((inputs) => ({
                          ...inputs,
                          [system.subtype]: value,
                        }));
                        setCardErrors((errors) => ({
                          ...errors,
                          [system.subtype]: value < 0 ? "Tonnage cannot be negative" : null,
                        }));
                      }}
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                      min="0"
                      step="1"
                      disabled={isInstalled}
                    />
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Total Ramscoop: <b className="text-gray-900 dark:text-gray-100">{Math.max(10, Math.ceil(moduleConfig.size * 0.01 + 5)) + (inputValue || 0)} tons</b>
                    </div>
                  </div>
                )}
                
                {error && (
                  <div className="text-xs text-red-600 mb-2">{error}</div>
                )}
              </div>
              
              <div className="mt-2 flex gap-2">
                {!isInstalled ? (
                  <button
                    className={`w-full mt-2 px-3 py-1 rounded text-white font-semibold ${
                      isDisabled
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                    onClick={() => handleAddFuelSystem(system)}
                    disabled={isDisabled}
                    title={
                      isInstalled
                        ? "Already installed"
                        : isOverTechLevel
                        ? `Requires TL ${system.techLevel}`
                        : wouldExceedTonnage
                        ? "Would exceed module tonnage"
                        : error
                        ? error
                        : "Add system"
                    }
                  >
                    Add
                  </button>
                ) : (
                  <button
                    className="px-4 py-1 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                    onClick={() => {
                      dispatch(removeModuleSystem({
                        systemType: "optionalSystems",
                        systemId: system.id,
                      }));
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Wrapper component for Accommodation Systems
 */
const AccommodationSystemsWrapper: React.FC<{ moduleConfig: any }> = ({ moduleConfig }) => {
  const dispatch = useAppDispatch();
  const installedSystems = useAppSelector((state) => state.module.moduleConfig?.systems.optionalSystems || []);
  
  // State for per-card inputs
  const [cardQuantities, setCardQuantities] = useState<{ [subtype: string]: number }>({});
  const [cardPassengers, setCardPassengers] = useState<{ [subtype: string]: number }>({});
  const [cardDesignations, setCardDesignations] = useState<{ [subtype: string]: string }>({});
  
  // State for grouped systems
  const [selectedAcceleration, setSelectedAcceleration] = useState("accelerationBench");
  const [selectedStateroom, setSelectedStateroom] = useState("highStateroom");
  const [accelerationQuantity, setAccelerationQuantity] = useState(1);
  const [stateroomQuantity, setStateroomQuantity] = useState(1);
  
  // State for MES popup
  const [showMESEditor, setShowMESEditor] = useState(false);
  const [editingMESSystem, setEditingMESSystem] = useState<any>(null);
  
  const allSystems = OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "accommodation");
  
  // Group systems
  const accelerationBench = allSystems.find(s => s.subtype === "accelerationBench");
  const accelerationSeat = allSystems.find(s => s.subtype === "accelerationSeat");
  const accelerationOptions = [accelerationBench, accelerationSeat].filter(Boolean);
  const selectedAccelerationSystem = accelerationOptions.find(s => s?.subtype === selectedAcceleration);
  
  const highStateroom = allSystems.find(s => s.subtype === "highStateroom");
  const luxuryStateroom = allSystems.find(s => s.subtype === "luxuryStateroom");
  const stateroomOptions = [highStateroom, luxuryStateroom].filter(Boolean);
  const selectedStateroomSystem = stateroomOptions.find(s => s?.subtype === selectedStateroom);
  
  // Individual systems (not grouped)
  const individualSystems = allSystems.filter(system => 
    !["accelerationBench", "accelerationSeat", "highStateroom", "luxuryStateroom"].includes(system.subtype)
  );
  
  const handleAddAccommodationSystem = (system: any, quantity?: number, designation?: string) => {
    const inputValue = quantity || cardQuantities[system.subtype] || cardPassengers[system.subtype] || 1;
    const finalDesignation = designation || cardDesignations[system.subtype] || "passenger";
    
    let calculatedCost = system.cost;
    let calculatedTonnage = system.tonnage;
    let calculatedPower = system.power;
    
    // Calculate stats based on system type
    switch (system.subtype) {
      case "barracks":
        calculatedTonnage = inputValue * 0.5; // 0.5 tons per person
        calculatedCost = inputValue * 50000; // Cr50,000 per person
        calculatedPower = 0;
        break;
      case "brig":
        calculatedTonnage = inputValue * 2; // 2 tons per cell
        calculatedCost = inputValue * 100000; // Cr100,000 per cell
        calculatedPower = 0;
        break;
      case "cabinSpace":
        calculatedTonnage = inputValue * 1; // 1 ton per passenger
        calculatedCost = inputValue * 100000; // Cr100,000 per passenger
        calculatedPower = 0;
        break;
      case "multiEnvironmentSpace":
        calculatedTonnage = 10; // Fixed 10 tons
        calculatedCost = 1000000; // Cr1,000,000
        calculatedPower = 5; // 5 power
        break;
      default:
        // For other systems, use quantity multiplier
        calculatedCost = system.cost * inputValue;
        calculatedTonnage = system.tonnage * inputValue;
        calculatedPower = system.power * inputValue;
    }
    
    const finalSystem = {
      ...system,
      cost: calculatedCost,
      tonnage: calculatedTonnage,
      power: calculatedPower,
      properties: {
        ...system.properties,
        quantity: inputValue,
        designation: finalDesignation,
      },
      id: `${system.id}_${Date.now()}`, // Ensure unique ID
    };
    
    dispatch(addModuleSystem({
      systemType: "optionalSystems",
      system: finalSystem,
    }));
  };
  
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString();
  };
  
  const handleMESConfirm = (updatedSystem: any) => {
    dispatch(addModuleSystem({
      systemType: "optionalSystems",
      system: updatedSystem,
    }));
    setShowMESEditor(false);
    setEditingMESSystem(null);
  };
  
  const handleMESCancel = () => {
    setShowMESEditor(false);
    setEditingMESSystem(null);
  };
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Accommodation Systems</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Acceleration Systems (Grouped) */}
        {accelerationOptions.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Acceleration Systems</div>
              <select
                className="w-full mb-2 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm dark:bg-gray-600 dark:text-gray-100"
                value={selectedAcceleration}
                onChange={(e) => setSelectedAcceleration(e.target.value)}
              >
                {accelerationOptions.map((opt) => (
                  <option key={opt?.subtype} value={opt?.subtype}>
                    {opt?.name}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <div>Cost: {formatCurrency(selectedAccelerationSystem?.cost || 0)} Cr</div>
                <div>Tonnage: {selectedAccelerationSystem?.tonnage || 0} tons</div>
                <div>Power: {selectedAccelerationSystem?.power || 0} Power</div>
              </div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Quantity
              </label>
              <input
                type="number"
                value={accelerationQuantity}
                onChange={(e) => setAccelerationQuantity(Math.max(1, Number(e.target.value)))}
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                min="1"
                step="1"
              />
            </div>
            <button
              className="mt-4 w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              onClick={() => selectedAccelerationSystem && handleAddAccommodationSystem(selectedAccelerationSystem, accelerationQuantity)}
            >
              Add
            </button>
          </div>
        )}
        
        {/* Staterooms (Grouped) */}
        {stateroomOptions.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Staterooms</div>
              <select
                className="w-full mb-2 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm dark:bg-gray-600 dark:text-gray-100"
                value={selectedStateroom}
                onChange={(e) => setSelectedStateroom(e.target.value)}
              >
                {stateroomOptions.map((opt) => (
                  <option key={opt?.subtype} value={opt?.subtype}>
                    {opt?.name}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <div>Cost: {formatCurrency(selectedStateroomSystem?.cost || 0)} Cr</div>
                <div>Tonnage: {selectedStateroomSystem?.tonnage || 0} tons</div>
                <div>Power: {selectedStateroomSystem?.power || 0} Power</div>
              </div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Quantity
              </label>
              <input
                type="number"
                value={stateroomQuantity}
                onChange={(e) => setStateroomQuantity(Math.max(1, Number(e.target.value)))}
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                min="1"
                step="1"
              />
            </div>
            <button
              className="mt-4 w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              onClick={() => selectedStateroomSystem && handleAddAccommodationSystem(selectedStateroomSystem, stateroomQuantity)}
            >
              Add
            </button>
          </div>
        )}
        
        {/* Individual Systems */}
        {individualSystems.map((system) => {
          const isInstalled = installedSystems.some(s => s.id.startsWith(system.id + '_'));
          const isOverTechLevel = system.techLevel > moduleConfig.techLevel;
          const totalTons = installedSystems.reduce((sum, sys) => sum + (sys.tonnage || 0), 0);
          const inputValue = cardQuantities[system.subtype] || cardPassengers[system.subtype] || 1;
          const designation = cardDesignations[system.subtype] || "passenger";
          
          // Calculate display values
          let displayCost = system.cost;
          let displayTonnage = system.tonnage;
          let displayPower = system.power;
          
          switch (system.subtype) {
            case "barracks":
              displayTonnage = inputValue * 0.5;
              displayCost = inputValue * 50000;
              break;
            case "brig":
              displayTonnage = inputValue * 2;
              displayCost = inputValue * 100000;
              break;
            case "cabinSpace":
              displayTonnage = inputValue * 1;
              displayCost = inputValue * 100000;
              break;
            case "multiEnvironmentSpace":
              displayTonnage = 10;
              displayCost = 1000000;
              displayPower = 5;
              break;
            default:
              displayCost = system.cost * inputValue;
              displayTonnage = system.tonnage * inputValue;
              displayPower = system.power * inputValue;
          }
          
          const wouldExceedTonnage = (totalTons + displayTonnage) > moduleConfig.size;
          const isDisabled = isInstalled || isOverTechLevel || wouldExceedTonnage;
          
          return (
            <div
              key={system.id}
              className={`border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between ${
                isDisabled ? "bg-gray-100 dark:bg-gray-800 opacity-60" : "bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
              }`}
            >
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{system.name}</div>
                
                <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <div>Cost: {formatCurrency(displayCost)} Cr</div>
                  <div>Tonnage: {displayTonnage.toFixed(1)} tons</div>
                  <div>Power: {displayPower} Power</div>
                  <div>TL: {system.techLevel}</div>
                </div>
                
                {/* System-specific input fields */}
                {system.subtype === "barracks" && (
                  <div>
                    <div className="mb-2">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Designation
                      </label>
                      <select
                        value={designation}
                        onChange={(e) =>
                          setCardDesignations((p) => ({
                            ...p,
                            [system.subtype]: e.target.value,
                          }))
                        }
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                        disabled={isInstalled}
                      >
                        <option value="passenger">Basic Passage</option>
                        <option value="crew">Crew Accommodation</option>
                      </select>
                    </div>
                    <div className="mb-2">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {designation === "crew" ? "Number of Crew" : "Number of Basic Passengers"}
                      </label>
                      <input
                        type="number"
                        value={inputValue}
                        onChange={(e) =>
                          setCardPassengers((p) => ({
                            ...p,
                            [system.subtype]: Math.max(1, Number(e.target.value)),
                          }))
                        }
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm"
                        min="1"
                        step="1"
                        disabled={isInstalled}
                      />
                    </div>
                  </div>
                )}
                
                {system.subtype === "brig" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Quantity
                    </label>
                    <input
                      type="number"
                      value={inputValue}
                      onChange={(e) =>
                        setCardQuantities((q) => ({
                          ...q,
                          [system.subtype]: Math.max(1, Number(e.target.value)),
                        }))
                      }
                      className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm"
                      min="1"
                      step="1"
                      disabled={isInstalled}
                    />
                  </div>
                )}
                
                {system.subtype === "cabinSpace" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Number of Passengers
                    </label>
                    <input
                      type="number"
                      value={inputValue}
                      onChange={(e) =>
                        setCardPassengers((p) => ({
                          ...p,
                          [system.subtype]: Math.max(1, Number(e.target.value)),
                        }))
                      }
                      className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm"
                      min="1"
                      step="1"
                      disabled={isInstalled}
                    />
                  </div>
                )}
                
                {system.subtype === "multiEnvironmentSpace" && (
                  <div>
                  </div>
                )}
              </div>
              
              <div className="mt-2 flex gap-2">
                {!isInstalled ? (
                  <button
                    className={`w-full mt-2 px-3 py-1 rounded text-white font-semibold ${
                      isDisabled
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                    onClick={() => {
                      if (system.subtype === "multiEnvironmentSpace") {
                        setEditingMESSystem(system);
                        setShowMESEditor(true);
                      } else {
                        handleAddAccommodationSystem(system);
                      }
                    }}
                    disabled={isDisabled}
                    title={
                      isInstalled
                        ? "Already installed"
                        : isOverTechLevel
                        ? `Requires TL ${system.techLevel}`
                        : wouldExceedTonnage
                        ? "Would exceed module tonnage"
                        : system.subtype === "multiEnvironmentSpace"
                        ? "Configure Multi-Environment Space"
                        : "Add system"
                    }
                  >
                    {system.subtype === "multiEnvironmentSpace" ? "Configure" : "Add"}
                  </button>
                ) : (
                  <button
                    className="px-4 py-1 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                    onClick={() => {
                      dispatch(removeModuleSystem({
                        systemType: "optionalSystems",
                        systemId: system.id,
                      }));
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* MES Inline Editor Modal */}
      {showMESEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-4">
              <MESInlineEditor
                availableSystems={allSystems}
                shipBuilderState={moduleConfig}
                onConfirm={handleMESConfirm}
                onCancel={handleMESCancel}
                editingSystem={editingMESSystem}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Wrapper component for Bridge Systems
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const BridgeSystemsWrapper: React.FC<{ moduleConfig: any }> = ({ moduleConfig }) => {
  const systems = OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "bridge");
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Bridge Systems</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {systems.map((system) => (
          <SystemCard key={system.id} system={system} moduleConfig={moduleConfig} />
        ))}
      </div>
    </div>
  );
};

/**
 * Wrapper component for Cargo Systems
 */
const CargoSystemsWrapper: React.FC<{ moduleConfig: any }> = ({ moduleConfig }) => {
  const dispatch = useAppDispatch();
  const installedSystems = useAppSelector((state) => state.module.moduleConfig?.systems.optionalSystems || []);
  
  // State for grouped systems
  const [jumpNetType, setJumpNetType] = useState<"jumpNetInterplanetary" | "jumpNetInterstellar">("jumpNetInterplanetary");
  const [loadingBeltType, setLoadingBeltType] = useState<"loadingBelt7" | "loadingBelt12">("loadingBelt7");
  
  // State for dynamic inputs
  const [jumpNetCargoCapacity, setJumpNetCargoCapacity] = useState<number>(0);
  const [externalCargoCapacity, setExternalCargoCapacity] = useState<number>(0);
  
  const allSystems = OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "cargo");
  
  // Group systems
  const jumpNetInterplanetary = allSystems.find(s => s.subtype === "jumpNetInterplanetary");
  const jumpNetInterstellar = allSystems.find(s => s.subtype === "jumpNetInterstellar");
  const jumpNetOptions = [jumpNetInterplanetary, jumpNetInterstellar].filter(Boolean);
  const selectedJumpNetSystem = jumpNetOptions.find(s => s?.subtype === jumpNetType);
  
  const loadingBelt7 = allSystems.find(s => s.subtype === "loadingBelt7");
  const loadingBelt12 = allSystems.find(s => s.subtype === "loadingBelt12");
  const loadingBeltOptions = [loadingBelt7, loadingBelt12].filter(Boolean);
  const selectedLoadingBeltSystem = loadingBeltOptions.find(s => s?.subtype === loadingBeltType);
  
  // Individual systems (not grouped)
  const individualSystems = allSystems.filter(system => 
    !["jumpNetInterplanetary", "jumpNetInterstellar", "loadingBelt7", "loadingBelt12"].includes(system.subtype)
  );
  
  const handleAddCargoSystem = (system: any, capacity?: number) => {
    let calculatedCost = system.cost;
    let calculatedTonnage = system.tonnage;
    let calculatedPower = system.power;
    
    // Calculate stats based on system type
    switch (system.subtype) {
      case "jumpNetInterplanetary":
        calculatedCost = (capacity || 0) * 100000; // Cr100,000 per ton
        calculatedTonnage = (capacity || 0) / 100; // 1 ton per 100 tons of cargo
        calculatedPower = 0;
        break;
      case "jumpNetInterstellar":
        calculatedCost = (capacity || 0) * 300000; // Cr300,000 per ton
        calculatedTonnage = (capacity || 0) / 100; // 1 ton per 100 tons of cargo
        calculatedPower = 0;
        break;
      case "externalCargoMount":
        calculatedCost = (capacity || 0) * 50000; // Cr50,000 per ton
        calculatedTonnage = (capacity || 0) * 0.1; // 10% of cargo capacity
        calculatedPower = 0;
        break;
      case "cargoCrane":
        // Cargo Crane has fixed stats but can be installed multiple times
        calculatedCost = system.cost;
        calculatedTonnage = system.tonnage;
        calculatedPower = system.power;
        break;
      default:
        // For other systems, use base stats
        calculatedCost = system.cost;
        calculatedTonnage = system.tonnage;
        calculatedPower = system.power;
    }
    
    const finalSystem = {
      ...system,
      cost: calculatedCost,
      tonnage: calculatedTonnage,
      power: calculatedPower,
      properties: {
        ...system.properties,
        cargoCapacity: capacity,
      },
      id: `${system.id}_${Date.now()}`, // Ensure unique ID
    };
    
    dispatch(addModuleSystem({
      systemType: "optionalSystems",
      system: finalSystem,
    }));
  };
  
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString();
  };
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Cargo Systems</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Jump Net (Grouped) */}
        {jumpNetOptions.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Jump Net</div>
              <select
                className="w-full mb-2 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm dark:bg-gray-600 dark:text-gray-100"
                value={jumpNetType}
                onChange={(e) => setJumpNetType(e.target.value as "jumpNetInterplanetary" | "jumpNetInterstellar")}
              >
                {jumpNetOptions.map((opt) => (
                  <option key={opt?.subtype} value={opt?.subtype}>
                    {opt?.subtype === "jumpNetInterplanetary" ? "Interplanetary" : "Interstellar"}
                  </option>
                ))}
              </select>
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Jump Net Cargo Capacity (tons)
                </label>
                <input
                  type="number"
                  value={jumpNetCargoCapacity || ""}
                  onChange={(e) => setJumpNetCargoCapacity(Math.max(0, Number(e.target.value)))}
                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                  min="0"
                  step="0.1"
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {jumpNetType === "jumpNetInterplanetary"
                    ? "Cr100,000 per ton, 1 per 100 tons"
                    : "Cr300,000 per ton, 1 per 100 tons"}
                </span>
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <div>Cost: {formatCurrency(jumpNetCargoCapacity * (jumpNetType === "jumpNetInterplanetary" ? 100000 : 300000))} Cr</div>
                <div>Tonnage: {(jumpNetCargoCapacity / 100).toFixed(1)} tons</div>
                <div>Power: 0 Power</div>
                <div>TL: {selectedJumpNetSystem?.techLevel}</div>
              </div>
            </div>
            <button
              className="mt-4 w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              onClick={() => selectedJumpNetSystem && handleAddCargoSystem(selectedJumpNetSystem, jumpNetCargoCapacity)}
              disabled={!jumpNetCargoCapacity || jumpNetCargoCapacity <= 0}
            >
              Add
            </button>
          </div>
        )}
        
        {/* Loading Belt (Grouped) */}
        {loadingBeltOptions.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Loading Belt</div>
              <select
                className="w-full mb-2 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm dark:bg-gray-600 dark:text-gray-100"
                value={loadingBeltType}
                onChange={(e) => setLoadingBeltType(e.target.value as "loadingBelt7" | "loadingBelt12")}
              >
                {loadingBeltOptions.map((opt) => (
                  <option key={opt?.subtype} value={opt?.subtype}>
                    {opt?.subtype === "loadingBelt7" ? "10 crew" : "25 crew"}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <div>Cost: {formatCurrency(selectedLoadingBeltSystem?.cost || 0)} Cr</div>
                <div>Tonnage: {selectedLoadingBeltSystem?.tonnage || 0} tons</div>
                <div>Power: {selectedLoadingBeltSystem?.power || 0} Power</div>
                <div>TL: {selectedLoadingBeltSystem?.techLevel}</div>
              </div>
            </div>
            <button
              className="mt-4 w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              onClick={() => selectedLoadingBeltSystem && handleAddCargoSystem(selectedLoadingBeltSystem)}
            >
              Add
            </button>
          </div>
        )}
        
        {/* Individual Systems */}
        {individualSystems.map((system) => {
          const isInstalled = installedSystems.some(s => s.id.startsWith(system.id + '_'));
          const isOverTechLevel = system.techLevel > moduleConfig.techLevel;
          const totalTons = installedSystems.reduce((sum, sys) => sum + (sys.tonnage || 0), 0);
          
          // Calculate display values
          let displayCost = system.cost;
          let displayTonnage = system.tonnage;
          let displayPower = system.power;
          
          switch (system.subtype) {
            case "externalCargoMount":
              displayCost = externalCargoCapacity * 50000;
              displayTonnage = externalCargoCapacity * 0.1;
              break;
            case "cargoCrane":
              // Cargo Crane has fixed stats
              displayCost = system.cost;
              displayTonnage = system.tonnage;
              displayPower = system.power;
              break;
            default:
              displayCost = system.cost;
              displayTonnage = system.tonnage;
              displayPower = system.power;
          }
          
          const wouldExceedTonnage = (totalTons + displayTonnage) > moduleConfig.size;
          const isDisabled = isInstalled || isOverTechLevel || wouldExceedTonnage;
          
          return (
            <div
              key={system.id}
              className={`border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between ${
                isDisabled ? "bg-gray-100 dark:bg-gray-800 opacity-60" : "bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
              }`}
            >
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{system.name}</div>
                
                <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <div>Cost: {formatCurrency(displayCost)} Cr</div>
                  <div>Tonnage: {displayTonnage.toFixed(1)} tons</div>
                  <div>Power: {displayPower} Power</div>
                  <div>TL: {system.techLevel}</div>
                </div>
                
                {/* System-specific input fields */}
                {system.subtype === "externalCargoMount" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      External Cargo Capacity (tons)
                    </label>
                    <input
                      type="number"
                      value={externalCargoCapacity || ""}
                      onChange={(e) => setExternalCargoCapacity(Math.max(0, Number(e.target.value)))}
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                      min="0"
                      step="0.1"
                      disabled={isInstalled}
                    />
                  </div>
                )}
                
                {system.subtype === "cargoCrane" && (
                  <div>
                  </div>
                )}
                
                {system.subtype === "cargoNet" && (
                  <div>
                  </div>
                )}
                
                {system.subtype === "cargoScoop" && (
                  <div>
                  </div>
                )}
              </div>
              
              <div className="mt-2 flex gap-2">
                {!isInstalled ? (
                  <button
                    className={`w-full mt-2 px-3 py-1 rounded text-white font-semibold ${
                      isDisabled
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                    onClick={() => {
                      if (system.subtype === "externalCargoMount") {
                        handleAddCargoSystem(system, externalCargoCapacity);
                      } else {
                        handleAddCargoSystem(system);
                      }
                    }}
                    disabled={isDisabled || (system.subtype === "externalCargoMount" && !externalCargoCapacity)}
                    title={
                      isInstalled
                        ? "Already installed"
                        : isOverTechLevel
                        ? `Requires TL ${system.techLevel}`
                        : wouldExceedTonnage
                        ? "Would exceed module tonnage"
                        : system.subtype === "externalCargoMount" && !externalCargoCapacity
                        ? "Please specify cargo capacity"
                        : "Add system"
                    }
                  >
                    Add
                  </button>
                ) : (
                  <button
                    className="px-4 py-1 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                    onClick={() => {
                      dispatch(removeModuleSystem({
                        systemType: "optionalSystems",
                        systemId: system.id,
                      }));
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Wrapper component for External Systems
 */
const ExternalSystemsWrapper: React.FC<{ moduleConfig: any }> = ({ moduleConfig }) => {
  const dispatch = useAppDispatch();
  const installedSystems = useAppSelector((state) => state.module.moduleConfig?.systems.optionalSystems || []);
  
  // State for configuration popups
  const [showConfigPopup, setShowConfigPopup] = useState(false);
  const [configSystem, setConfigSystem] = useState<any>(null);
  const [configType, setConfigType] = useState<string>("");
  
  const allSystems = OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "external");
  
  const handleAddExternalSystem = (system: any, configData?: any) => {
    let calculatedCost = system.cost;
    let calculatedTonnage = system.tonnage;
    let calculatedPower = system.power;
    
    // Calculate stats based on system type and configuration
    switch (system.subtype) {
      case "dockingClamp":
        const clampTypes = {
          typeI: { cost: 50000, tonnage: 1, maxTonnage: 30 },
          typeII: { cost: 100000, tonnage: 2, maxTonnage: 99 },
          typeIII: { cost: 200000, tonnage: 3, maxTonnage: 300 },
          typeIV: { cost: 500000, tonnage: 5, maxTonnage: 2000 },
          typeV: { cost: 1000000, tonnage: 10, maxTonnage: 999999 },
        };
        const clampConfig = clampTypes[configData?.clampType as keyof typeof clampTypes] || clampTypes.typeI;
        calculatedCost = clampConfig.cost;
        calculatedTonnage = clampConfig.tonnage;
        calculatedPower = 0;
        break;
      case "forcedLinkage":
        const linkageTypes = {
          basic: { cost: 100000, tonnage: 2, pilotDM: -2 },
          improved: { cost: 200000, tonnage: 2, pilotDM: -1 },
          enhanced: { cost: 500000, tonnage: 2, pilotDM: 0 },
          advanced: { cost: 1000000, tonnage: 2, pilotDM: 2 },
        };
        const linkageConfig = linkageTypes[configData?.linkageType as keyof typeof linkageTypes] || linkageTypes.basic;
        calculatedCost = linkageConfig.cost;
        calculatedTonnage = linkageConfig.tonnage;
        calculatedPower = 0;
        break;
      case "grapplingArm":
        const armTypes = {
          standard: { cost: 50000, tonnage: 1, maxWeight: 2 },
          heavy: { cost: 200000, tonnage: 3, maxWeight: 10 },
        };
        const armConfig = armTypes[configData?.armType as keyof typeof armTypes] || armTypes.standard;
        calculatedCost = armConfig.cost;
        calculatedTonnage = armConfig.tonnage;
        calculatedPower = 0;
        break;
      default:
        // For other systems, use base stats
        calculatedCost = system.cost;
        calculatedTonnage = system.tonnage;
        calculatedPower = system.power;
    }
    
    const finalSystem = {
      ...system,
      cost: calculatedCost,
      tonnage: calculatedTonnage,
      power: calculatedPower,
      properties: {
        ...system.properties,
        ...configData,
      },
      id: `${system.id}_${Date.now()}`, // Ensure unique ID
    };
    
    dispatch(addModuleSystem({
      systemType: "optionalSystems",
      system: finalSystem,
    }));
    
    setShowConfigPopup(false);
    setConfigSystem(null);
    setConfigType("");
  };
  
  const handleConfigureSystem = (system: any) => {
    setConfigSystem(system);
    setConfigType(system.subtype);
    setShowConfigPopup(true);
  };
  
  const handleCancelConfig = () => {
    setShowConfigPopup(false);
    setConfigSystem(null);
    setConfigType("");
  };
  
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString();
  };
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">External Systems</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {allSystems.map((system) => {
          const isInstalled = installedSystems.some(s => s.id === system.id);
          const isOverTechLevel = system.techLevel > moduleConfig.techLevel;
          const totalTons = installedSystems.reduce((sum, sys) => sum + (sys.tonnage || 0), 0);
          
          // Calculate display values
          let displayCost = system.cost;
          let displayTonnage = system.tonnage;
          let displayPower = system.power;
          
          const wouldExceedTonnage = (totalTons + displayTonnage) > moduleConfig.size;
          const isDisabled = isInstalled || isOverTechLevel || wouldExceedTonnage;
          const needsConfiguration = ["dockingClamp", "forcedLinkage", "grapplingArm"].includes(system.subtype);
          
          return (
            <div
              key={system.id}
              className={`border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between ${
                isDisabled ? "bg-gray-100 dark:bg-gray-800 opacity-60" : "bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
              }`}
            >
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{system.name}</div>
                
                <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <div>Cost: {formatCurrency(displayCost)} Cr</div>
                  <div>Tonnage: {displayTonnage.toFixed(1)} tons</div>
                  <div>Power: {displayPower} Power</div>
                  <div>TL: {system.techLevel}</div>
                </div>
                
              </div>
              
              <div className="mt-2 flex gap-2">
                {!isInstalled ? (
                  <button
                    className={`w-full mt-2 px-3 py-1 rounded text-white font-semibold ${
                      isDisabled
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                    onClick={() => {
                      if (needsConfiguration) {
                        handleConfigureSystem(system);
                      } else {
                        handleAddExternalSystem(system);
                      }
                    }}
                    disabled={isDisabled}
                    title={
                      isInstalled
                        ? "Already installed"
                        : isOverTechLevel
                        ? `Requires TL ${system.techLevel}`
                        : wouldExceedTonnage
                        ? "Would exceed module tonnage"
                        : needsConfiguration
                        ? "Configure system"
                        : "Add system"
                    }
                  >
                    {needsConfiguration ? "Configure" : "Add"}
                  </button>
                ) : (
                  <button
                    className="px-4 py-1 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                    onClick={() => {
                      dispatch(removeModuleSystem({
                        systemType: "optionalSystems",
                        systemId: system.id,
                      }));
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Configuration Popup */}
      {showConfigPopup && configSystem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              Configure {configSystem.name}
            </h3>
            <div className="space-y-4">
              {configType === "dockingClamp" && (
                <DockingClampConfig
                  system={configSystem}
                  onConfigure={handleAddExternalSystem}
                  onCancel={handleCancelConfig}
                />
              )}
              {configType === "forcedLinkage" && (
                <ForcedLinkageConfig
                  system={configSystem}
                  onConfigure={handleAddExternalSystem}
                  onCancel={handleCancelConfig}
                />
              )}
              {configType === "grapplingArm" && (
                <GrapplingArmConfig
                  system={configSystem}
                  onConfigure={handleAddExternalSystem}
                  onCancel={handleCancelConfig}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Wrapper component for Internal Systems
 */
const InternalSystemsWrapper: React.FC<{ moduleConfig: any }> = ({ moduleConfig }) => {
  const dispatch = useAppDispatch();
  const installedSystems = useAppSelector((state) => state.module.moduleConfig?.systems.optionalSystems || []);
  
  // State for configuration popups
  const [showConfigPopup, setShowConfigPopup] = useState(false);
  const [configSystem, setConfigSystem] = useState<any>(null);
  const [configType, setConfigType] = useState<string>("");
  
  // State for capsule systems
  const [selectedCapsule, setSelectedCapsule] = useState<string>("internalReEntryCapsule");
  const [capsuleQty, setCapsuleQty] = useState<number>(1);
  
  const allSystems = OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "internal");
  
  // Get capsule options
  const capsuleOptions = [
    allSystems.find(s => s.subtype === "internalReEntryCapsule"),
    allSystems.find(s => s.subtype === "internalAssaultCapsule"),
    allSystems.find(s => s.subtype === "internalHighSurvivabilityCapsule"),
    allSystems.find(s => s.subtype === "internalReEntryPod"),
  ].filter(Boolean);
  const selectedCapsuleSystem = capsuleOptions.find(s => s?.subtype === selectedCapsule);
  
  const handleAddInternalSystem = (system: any, configData?: any) => {
    let calculatedCost = system.cost;
    let calculatedTonnage = system.tonnage;
    let calculatedPower = system.power;
    
    // Calculate stats based on system type and configuration
    switch (system.subtype) {
      case "internalAirlock":
        const airlockTonnage = configData?.tonnage || 1;
        calculatedCost = airlockTonnage * 50000; // Cr50,000 per ton
        calculatedTonnage = airlockTonnage;
        calculatedPower = 0;
        break;
      case "internalArmoury":
        // Armoury is calculated based on crew/marines, but for modules we'll use a default
        calculatedTonnage = configData?.tonnage || 1;
        calculatedCost = calculatedTonnage * 250000; // Cr250,000 per ton
        calculatedPower = 0;
        break;
      case "internalBiosphere":
        const biosphereTonnage = configData?.tonnage || 1;
        calculatedCost = biosphereTonnage * 1000000; // MCr1 per ton
        calculatedTonnage = biosphereTonnage;
        calculatedPower = biosphereTonnage * 2; // 2 power per ton
        break;
      case "internalBoobyTrap":
        const trappedCount = configData?.trappedCount || 1;
        const trapType = configData?.boobyTrapType || "basic";
        const trapCost = trapType === "basic" ? 100000 : trapType === "improved" ? 200000 : 500000;
        calculatedCost = trapCost * trappedCount;
        calculatedTonnage = 0; // No additional tonnage
        calculatedPower = 0;
        break;
      case "internalConcealedCompartment":
        const compartmentTonnage = configData?.tonnage || 1;
        calculatedCost = compartmentTonnage * 500000; // Cr500,000 per ton
        calculatedTonnage = compartmentTonnage;
        calculatedPower = 0;
        break;
      case "internalDockingSpace":
        const dockingTonnage = configData?.tonnage || 1;
        calculatedCost = dockingTonnage * 100000; // Cr100,000 per ton
        calculatedTonnage = dockingTonnage;
        calculatedPower = 0;
        break;
      case "internalFullHangar":
        const hangarTonnage = configData?.tonnage || 1;
        calculatedCost = hangarTonnage * 50000; // Cr50,000 per ton
        calculatedTonnage = hangarTonnage;
        calculatedPower = 0;
        break;
      case "internalLaboratory":
        const labTonnage = configData?.tonnage || 1;
        calculatedCost = labTonnage * 1000000; // MCr1 per ton
        calculatedTonnage = labTonnage;
        calculatedPower = labTonnage * 2; // 2 power per ton
        break;
      case "internalLaunchTube":
        const tubeTonnage = configData?.tonnage || 1;
        calculatedCost = tubeTonnage * 200000; // Cr200,000 per ton
        calculatedTonnage = tubeTonnage;
        calculatedPower = 0;
        break;
      case "internalRecoveryDeck":
        const recoveryTonnage = configData?.tonnage || 1;
        calculatedCost = recoveryTonnage * 100000; // Cr100,000 per ton
        calculatedTonnage = recoveryTonnage;
        calculatedPower = 0;
        break;
      case "internalStudio":
        const studioTonnage = configData?.tonnage || 1;
        calculatedCost = studioTonnage * 500000; // Cr500,000 per ton
        calculatedTonnage = studioTonnage;
        calculatedPower = studioTonnage * 1; // 1 power per ton
        break;
      case "internalTrainingFacilities":
        const trainingTonnage = configData?.tonnage || 1;
        calculatedCost = trainingTonnage * 200000; // Cr200,000 per ton
        calculatedTonnage = trainingTonnage;
        calculatedPower = trainingTonnage * 1; // 1 power per ton
        break;
      case "internalVault":
        const vaultTonnage = configData?.tonnage || 1;
        calculatedCost = vaultTonnage * 1000000; // MCr1 per ton
        calculatedTonnage = vaultTonnage;
        calculatedPower = 0;
        break;
      case "internalGravScreen":
        // Grav Screen is calculated based on module size
        const gravScreenTonnage = Math.max(1, Math.ceil(moduleConfig.size / 200)); // 1 ton per 200 tons
        calculatedCost = gravScreenTonnage * 1000000; // MCr1 per ton
        calculatedTonnage = gravScreenTonnage;
        calculatedPower = gravScreenTonnage * 2; // 2 power per ton
        break;
      default:
        // For other systems, use base stats
        calculatedCost = system.cost;
        calculatedTonnage = system.tonnage;
        calculatedPower = system.power;
    }
    
    const finalSystem = {
      ...system,
      cost: calculatedCost,
      tonnage: calculatedTonnage,
      power: calculatedPower,
      properties: {
        ...system.properties,
        ...configData,
      },
      id: `${system.id}_${Date.now()}`, // Ensure unique ID
    };
    
    dispatch(addModuleSystem({
      systemType: "optionalSystems",
      system: finalSystem,
    }));
    
    setShowConfigPopup(false);
    setConfigSystem(null);
    setConfigType("");
  };
  
  const handleConfigureSystem = (system: any) => {
    setConfigSystem(system);
    setConfigType(system.subtype);
    setShowConfigPopup(true);
  };
  
  const handleCancelConfig = () => {
    setShowConfigPopup(false);
    setConfigSystem(null);
    setConfigType("");
  };
  
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString();
  };
  
  // Define system groups
  const systemGroups: Record<string, string[]> = {
    "Emergency Systems": [
      "internalReEntryCapsule",
      "internalAssaultCapsule", 
      "internalHighSurvivabilityCapsule",
      "internalReEntryPod",
    ],
    "Access & Security": [
      "internalAirlock",
      "internalBoobyTrap",
      "internalConcealedCompartment",
      "internalVault",
    ],
    "Craft Operations": [
      "internalLaunchTube",
      "internalRecoveryDeck",
      "internalDockingSpace",
      "internalFullHangar",
      "internalConstructionDeck",
    ],
    "Crew Facilities": [
      "internalArmoury",
      "internalBriefingRoom",
      "internalLibrary",
      "internalTrainingFacilities",
    ],
    "Life Support & Accommodation": [
      "internalBiosphere",
      "internalStable",
      "internalMedicalBay",
    ],
    "Professional Spaces": [
      "internalLaboratory",
      "internalStudio",
      "internalWorkshop",
    ],
    "Specialized Systems": [
      "internalGravScreen",
      "internalUnrepSystem",
    ],
  };
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Internal Systems</h3>
      
      {/* Emergency Systems (Capsules) */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-blue-700 dark:text-blue-300 border-b border-blue-200 dark:border-blue-600 pb-2">
          Emergency Systems
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 min-h-[220px]">
            <div>
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Capsule</h4>
                <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                  TL{selectedCapsuleSystem?.techLevel}
                </span>
              </div>
              <select
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm mb-2 dark:bg-gray-600 dark:text-gray-100"
                value={selectedCapsule}
                onChange={(e) => setSelectedCapsule(e.target.value)}
              >
                {capsuleOptions.map((opt) => (
                  <option key={opt?.subtype} value={opt?.subtype}>
                    {opt?.name}
                  </option>
                ))}
              </select>
              <div className="mb-2">
                <TonnageInput
                  value={capsuleQty * 0.5} // Convert quantity to tons (0.5 tons per capsule)
                  onChange={(tons) => setCapsuleQty(Math.max(1, Math.ceil(tons / 0.5)))} // Convert tons back to quantity
                  systemType="reentryCapsule"
                  category="optionalSystems"
                  label="Total Tonnage"
                  placeholder="Enter tonnage"
                  className="text-sm"
                  showValidation={true}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Quantity: {capsuleQty} capsule{capsuleQty !== 1 ? 's' : ''} (0.5 tons each)
                </p>
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <div><b>Cost:</b> {formatCurrency(selectedCapsuleSystem?.cost ?? 0)} Cr</div>
                <div><b>Tonnage:</b> {selectedCapsuleSystem?.tonnage ?? 0} tons</div>
                <div><b>Power:</b> {selectedCapsuleSystem?.power ?? 0} Power</div>
              </div>
            </div>
            <div className="mt-2">
              <button
                className="w-full mt-2 px-3 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  for (let i = 0; i < capsuleQty; i++) {
                    const sys = {
                      ...selectedCapsuleSystem!,
                      id: `${selectedCapsuleSystem?.id}_${Date.now()}_${i}`,
                    };
                    dispatch(addModuleSystem({
                      systemType: "optionalSystems",
                      system: sys,
                    }));
                  }
                }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Other System Groups */}
      {Object.entries(systemGroups).map(([groupName, systemSubtypes]) => {
        if (groupName === "Emergency Systems") return null; // Already handled above
        
        const groupSystems = allSystems
          .filter(system => systemSubtypes.includes(system.subtype))
          .sort((a, b) => a.name.localeCompare(b.name));
        
        if (groupSystems.length === 0) return null;
        
        return (
          <div key={groupName} className="mb-6">
            <h4 className="text-lg font-semibold mb-3 text-blue-700 border-b border-blue-200 pb-2">
              {groupName}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {groupSystems.map((system) => {
                const isInstalled = installedSystems.some(s => s.id === system.id);
                const isOverTechLevel = system.techLevel > moduleConfig.techLevel;
                const totalTons = installedSystems.reduce((sum, sys) => sum + (sys.tonnage || 0), 0);
                
                // Calculate display values
                let displayCost = system.cost;
                let displayTonnage = system.tonnage;
                let displayPower = system.power;
                
                // Special handling for Grav Screen
                if (system.subtype === "internalGravScreen") {
                  const gravScreenTonnage = Math.max(1, Math.ceil(moduleConfig.size / 200));
                  displayCost = gravScreenTonnage * 1000000;
                  displayTonnage = gravScreenTonnage;
                  displayPower = gravScreenTonnage * 2;
                }
                
                const wouldExceedTonnage = (totalTons + displayTonnage) > moduleConfig.size;
                const isDisabled = isInstalled || isOverTechLevel || wouldExceedTonnage;
                const needsConfiguration = [
                  "internalAirlock",
                  "internalArmoury", 
                  "internalBiosphere",
                  "internalBoobyTrap",
                  "internalConcealedCompartment",
                  "internalDockingSpace",
                  "internalFullHangar",
                  "internalLaboratory",
                  "internalLaunchTube",
                  "internalRecoveryDeck",
                  "internalStudio",
                  "internalTrainingFacilities",
                  "internalVault",
                ].includes(system.subtype);
                
                return (
                  <div
                    key={system.id}
                    className={`border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 min-h-[220px] ${
                      isDisabled ? "opacity-60" : ""
                    }`}
                  >
                    <div>
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{system.name}</h4>
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                          TL{system.techLevel}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                        <div><b>Cost:</b> {formatCurrency(displayCost)} Cr</div>
                        <div><b>Tonnage:</b> {displayTonnage.toFixed(1)} tons</div>
                        <div><b>Power:</b> {displayPower} Power</div>
                      </div>
                    </div>
                    <div className="mt-2">
                      {!isInstalled ? (
                        <button
                          className={`w-full mt-2 px-3 py-1 rounded text-white font-semibold ${
                            isDisabled
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-blue-600 hover:bg-blue-700"
                          }`}
                          onClick={() => {
                            if (needsConfiguration) {
                              handleConfigureSystem(system);
                            } else {
                              handleAddInternalSystem(system);
                            }
                          }}
                          disabled={isDisabled}
                          title={
                            isInstalled
                              ? "Already installed"
                              : isOverTechLevel
                              ? `Requires TL ${system.techLevel}`
                              : wouldExceedTonnage
                              ? "Would exceed module tonnage"
                              : needsConfiguration
                              ? "Configure system"
                              : "Add system"
                          }
                        >
                          {needsConfiguration ? "Configure" : "Add"}
                        </button>
                      ) : (
                        <button
                          className="px-4 py-1 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                          onClick={() => {
                            dispatch(removeModuleSystem({
                              systemType: "optionalSystems",
                              systemId: system.id,
                            }));
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      
      {/* Configuration Popup */}
      {showConfigPopup && configSystem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              Configure {configSystem.name}
            </h3>
            <div className="space-y-4">
              {configType === "internalAirlock" && (
                <AirlockConfig
                  system={configSystem}
                  onConfigure={handleAddInternalSystem}
                  onCancel={handleCancelConfig}
                />
              )}
              {configType === "internalArmoury" && (
                <ArmouryConfig
                  system={configSystem}
                  onConfigure={handleAddInternalSystem}
                  onCancel={handleCancelConfig}
                />
              )}
              {configType === "internalBiosphere" && (
                <BiosphereConfig
                  system={configSystem}
                  onConfigure={handleAddInternalSystem}
                  onCancel={handleCancelConfig}
                />
              )}
              {configType === "internalBoobyTrap" && (
                <BoobyTrapConfig
                  system={configSystem}
                  onConfigure={handleAddInternalSystem}
                  onCancel={handleCancelConfig}
                  moduleConfig={moduleConfig}
                  installedSystems={installedSystems}
                />
              )}
              {configType === "internalConcealedCompartment" && (
                <ConcealedCompartmentConfig
                  system={configSystem}
                  onConfigure={handleAddInternalSystem}
                  onCancel={handleCancelConfig}
                  moduleConfig={moduleConfig}
                />
              )}
              {configType === "internalDockingSpace" && (
                <DockingSpaceConfig
                  system={configSystem}
                  onConfigure={handleAddInternalSystem}
                  onCancel={handleCancelConfig}
                />
              )}
              {configType === "internalFullHangar" && (
                <FullHangarConfig
                  system={configSystem}
                  onConfigure={handleAddInternalSystem}
                  onCancel={handleCancelConfig}
                />
              )}
              {configType === "internalLaboratory" && (
                <LaboratoryConfig
                  system={configSystem}
                  onConfigure={handleAddInternalSystem}
                  onCancel={handleCancelConfig}
                />
              )}
              {configType === "internalLaunchTube" && (
                <LaunchTubeConfig
                  system={configSystem}
                  onConfigure={handleAddInternalSystem}
                  onCancel={handleCancelConfig}
                />
              )}
              {configType === "internalRecoveryDeck" && (
                <RecoveryDeckConfig
                  system={configSystem}
                  onConfigure={handleAddInternalSystem}
                  onCancel={handleCancelConfig}
                />
              )}
              {configType === "internalStudio" && (
                <StudioConfig
                  system={configSystem}
                  onConfigure={handleAddInternalSystem}
                  onCancel={handleCancelConfig}
                />
              )}
              {configType === "internalTrainingFacilities" && (
                <TrainingFacilitiesConfig
                  system={configSystem}
                  onConfigure={handleAddInternalSystem}
                  onCancel={handleCancelConfig}
                />
              )}
              {configType === "internalVault" && (
                <VaultConfig
                  system={configSystem}
                  onConfigure={handleAddInternalSystem}
                  onCancel={handleCancelConfig}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Wrapper component for Sensor Systems
 */
const SensorSystemsWrapper: React.FC<{ moduleConfig: any }> = ({ moduleConfig }) => {
  const dispatch = useAppDispatch();
  const installedSystems = useAppSelector((state) => state.module.moduleConfig?.systems.optionalSystems || []);
  
  // State for grouped systems
  const [selectedCountermeasure, setSelectedCountermeasure] = useState("countermeasuresSuite");
  const [selectedLifeScanner, setSelectedLifeScanner] = useState("lifeScanner");
  const [selectedMailDistribution, setSelectedMailDistribution] = useState("mailDistributionArray10");
  const [selectedSignalProcessing, setSelectedSignalProcessing] = useState("improvedSignalProcessing");
  
  // State for custom tonnage inputs
  const [customTons, setCustomTons] = useState<{ [id: string]: number }>({});
  
  const allSystems = OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "sensor");
  
  // Define grouped system IDs
  const COUNTERMEASURE_IDS = ["countermeasuresSuite", "militaryCountermeasuresSuite"];
  const LIFE_SCANNER_IDS = ["lifeScanner", "lifeScannerAnalysisSuite"];
  const MAIL_DISTRIBUTION_IDS = ["mailDistributionArray10", "mailDistributionArray13"];
  const SIGNAL_PROCESSING_IDS = ["improvedSignalProcessing", "enhancedSignalProcessing"];
  
  // Get grouped system options
  const countermeasureOptions = allSystems.filter(s => COUNTERMEASURE_IDS.includes(s.id));
  const selectedCountermeasureObj = countermeasureOptions.find(s => s.id === selectedCountermeasure);
  
  const lifeScannerOptions = allSystems.filter(s => LIFE_SCANNER_IDS.includes(s.id));
  const selectedLifeScannerObj = lifeScannerOptions.find(s => s.id === selectedLifeScanner);
  
  const mailDistributionOptions = allSystems.filter(s => MAIL_DISTRIBUTION_IDS.includes(s.id));
  const selectedMailDistributionObj = mailDistributionOptions.find(s => s.id === selectedMailDistribution);
  
  const signalProcessingOptions = allSystems.filter(s => SIGNAL_PROCESSING_IDS.includes(s.id));
  const selectedSignalProcessingObj = signalProcessingOptions.find(s => s.id === selectedSignalProcessing);
  
  // Filter out grouped systems from the main list
  const individualSystems = allSystems.filter(s => 
    !COUNTERMEASURE_IDS.includes(s.id) &&
    !LIFE_SCANNER_IDS.includes(s.id) &&
    !MAIL_DISTRIBUTION_IDS.includes(s.id) &&
    !SIGNAL_PROCESSING_IDS.includes(s.id)
  );
  
  const handleAddSensorSystem = (system: any) => {
    let calculatedCost = system.cost;
    let calculatedTonnage = system.tonnage;
    let calculatedPower = system.power;
    
    // Calculate stats based on system type
    switch (system.subtype) {
      case "deepPenetrationScanners":
        const scannerTons = customTons[system.id] || 1;
        calculatedCost = scannerTons * 1000000; // MCr1 per ton
        calculatedTonnage = scannerTons;
        calculatedPower = scannerTons * 2; // 2 power per ton
        break;
      case "extensionNet":
        calculatedCost = 500000; // Fixed cost
        calculatedTonnage = 1; // Fixed tonnage
        calculatedPower = 1; // Fixed power
        break;
      case "distributedArray":
        // Based on base sensor suite (simplified for modules)
        calculatedCost = 2000000; // Fixed cost
        calculatedTonnage = 2; // Fixed tonnage
        calculatedPower = 2; // Fixed power
        break;
      case "extendedArray":
        // Based on base sensor suite (simplified for modules)
        calculatedCost = 3000000; // Fixed cost
        calculatedTonnage = 3; // Fixed tonnage
        calculatedPower = 3; // Fixed power
        break;
      case "rapidDeploymentExtendedArray":
        // Based on base sensor suite (simplified for modules)
        calculatedCost = 5000000; // Fixed cost
        calculatedTonnage = 5; // Fixed tonnage
        calculatedPower = 5; // Fixed power
        break;
      default:
        // For other systems, use base stats
        calculatedCost = system.cost;
        calculatedTonnage = system.tonnage;
        calculatedPower = system.power;
    }
    
    const finalSystem = {
      ...system,
      cost: calculatedCost,
      tonnage: calculatedTonnage,
      power: calculatedPower,
      properties: {
        ...system.properties,
        customTonnage: customTons[system.id],
      },
      id: `${system.id}_${Date.now()}`, // Ensure unique ID
    };
    
    dispatch(addModuleSystem({
      systemType: "optionalSystems",
      system: finalSystem,
    }));
  };
  
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString();
  };
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Sensor Systems</h3>
      
      {/* Grouped Systems */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Countermeasures Card with Dropdown */}
        {countermeasureOptions.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Countermeasures Suite</div>
              <select
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm mb-2 dark:bg-gray-600 dark:text-gray-100"
                value={selectedCountermeasure}
                onChange={(e) => setSelectedCountermeasure(e.target.value)}
              >
                {countermeasureOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <div><b>Cost:</b> {formatCurrency(selectedCountermeasureObj?.cost || 0)} Cr</div>
                <div><b>Tonnage:</b> {selectedCountermeasureObj?.tonnage || 0} tons</div>
                <div><b>Power:</b> {selectedCountermeasureObj?.power || 0} Power</div>
              </div>
            </div>
            <button
              className="px-4 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
              onClick={() => selectedCountermeasureObj && handleAddSensorSystem(selectedCountermeasureObj)}
            >
              Add
            </button>
          </div>
        )}
        
        {/* Life Scanner Card with Dropdown */}
        {lifeScannerOptions.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Life Scanner</div>
              <select
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm mb-2 dark:bg-gray-600 dark:text-gray-100"
                value={selectedLifeScanner}
                onChange={(e) => setSelectedLifeScanner(e.target.value)}
              >
                {lifeScannerOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <div><b>Cost:</b> {formatCurrency(selectedLifeScannerObj?.cost || 0)} Cr</div>
                <div><b>Tonnage:</b> {selectedLifeScannerObj?.tonnage || 0} tons</div>
                <div><b>Power:</b> {selectedLifeScannerObj?.power || 0} Power</div>
              </div>
            </div>
            <button
              className="px-4 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
              onClick={() => selectedLifeScannerObj && handleAddSensorSystem(selectedLifeScannerObj)}
            >
              Add
            </button>
          </div>
        )}
        
        {/* Mail Distribution Array Card with Dropdown */}
        {mailDistributionOptions.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Mail Distribution Array</div>
              <select
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm mb-2 dark:bg-gray-600 dark:text-gray-100"
                value={selectedMailDistribution}
                onChange={(e) => setSelectedMailDistribution(e.target.value)}
              >
                {mailDistributionOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <div><b>Cost:</b> {formatCurrency(selectedMailDistributionObj?.cost || 0)} Cr</div>
                <div><b>Tonnage:</b> {selectedMailDistributionObj?.tonnage || 0} tons</div>
                <div><b>Power:</b> {selectedMailDistributionObj?.power || 0} Power</div>
              </div>
            </div>
            <button
              className="px-4 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
              onClick={() => selectedMailDistributionObj && handleAddSensorSystem(selectedMailDistributionObj)}
            >
              Add
            </button>
          </div>
        )}
        
        {/* Signal Processing Card with Dropdown */}
        {signalProcessingOptions.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Signal Processing</div>
              <select
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm mb-2 dark:bg-gray-600 dark:text-gray-100"
                value={selectedSignalProcessing}
                onChange={(e) => setSelectedSignalProcessing(e.target.value)}
              >
                {signalProcessingOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <div><b>Cost:</b> {formatCurrency(selectedSignalProcessingObj?.cost || 0)} Cr</div>
                <div><b>Tonnage:</b> {selectedSignalProcessingObj?.tonnage || 0} tons</div>
                <div><b>Power:</b> {selectedSignalProcessingObj?.power || 0} Power</div>
              </div>
            </div>
            <button
              className="px-4 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
              onClick={() => selectedSignalProcessingObj && handleAddSensorSystem(selectedSignalProcessingObj)}
            >
              Add
            </button>
          </div>
        )}
      </div>
      
      {/* Individual Systems */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {individualSystems.map((system) => {
          const isInstalled = installedSystems.some(s => s.id.startsWith(system.id + '_'));
          const isOverTechLevel = system.techLevel > moduleConfig.techLevel;
          const totalTons = installedSystems.reduce((sum, sys) => sum + (sys.tonnage || 0), 0);
          
          // Calculate display values
          let displayCost = system.cost;
          let displayTonnage = system.tonnage;
          let displayPower = system.power;
          
          // Special handling for deep penetration scanners
          if (system.subtype === "deepPenetrationScanners") {
            const scannerTons = customTons[system.id] || 1;
            displayCost = scannerTons * 1000000;
            displayTonnage = scannerTons;
            displayPower = scannerTons * 2;
          }
          
          const wouldExceedTonnage = (totalTons + displayTonnage) > moduleConfig.size;
          const isDisabled = isInstalled || isOverTechLevel || wouldExceedTonnage;
          
          return (
            <div
              key={system.id}
              className={`border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 ${
                isDisabled ? "opacity-60" : ""
              }`}
            >
              <div>
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{system.name}</h4>
                  <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                    TL{system.techLevel}
                  </span>
                </div>
                
                {/* Custom tonnage input for deep penetration scanners */}
                {system.subtype === "deepPenetrationScanners" && (
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Scanner Tonnage
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={customTons[system.id] || 1}
                      onChange={(e) => setCustomTons({
                        ...customTons,
                        [system.id]: Math.max(1, Number(e.target.value))
                      })}
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                      disabled={isInstalled}
                    />
                  </div>
                )}
                
                <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <div><b>Cost:</b> {formatCurrency(displayCost)} Cr</div>
                  <div><b>Tonnage:</b> {displayTonnage.toFixed(1)} tons</div>
                  <div><b>Power:</b> {displayPower} Power</div>
                </div>
              </div>
              <div className="mt-2">
                {!isInstalled ? (
                  <button
                    className={`w-full mt-2 px-3 py-1 rounded text-white font-semibold ${
                      isDisabled
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                    onClick={() => handleAddSensorSystem(system)}
                    disabled={isDisabled}
                    title={
                      isInstalled
                        ? "Already installed"
                        : isOverTechLevel
                        ? `Requires TL ${system.techLevel}`
                        : wouldExceedTonnage
                        ? "Would exceed module tonnage"
                        : "Add system"
                    }
                  >
                    Add
                  </button>
                ) : (
                  <button
                    className="px-4 py-1 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                    onClick={() => {
                      dispatch(removeModuleSystem({
                        systemType: "optionalSystems",
                        systemId: system.id,
                      }));
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Wrapper component for Common Areas Systems
 */
const CommonAreasSystemsWrapper: React.FC<{ moduleConfig: any }> = ({ moduleConfig }) => {
  const dispatch = useAppDispatch();
  const installedSystems = useAppSelector((state) => state.module.moduleConfig?.systems.optionalSystems || []);
  
  // State for grouped systems
  const [selectedSwimmingPool, setSelectedSwimmingPool] = useState("swimmingPool");
  const [selectedTheatre, setSelectedTheatre] = useState("theatreBasic");
  
  // State for custom inputs
  // const [customTons, setCustomTons] = useState<{ [id: string]: number }>({});
  const [entertainmentTechLevel, setEntertainmentTechLevel] = useState(5);
  const [entertainmentCost, setEntertainmentCost] = useState(1000);
  const [breweryTonnage, setBreweryTonnage] = useState(0.5);
  const [gourmetDiners, setGourmetDiners] = useState(1);
  const [hotTubUsers, setHotTubUsers] = useState(1);
  const [swimmingPoolTonnage, setSwimmingPoolTonnage] = useState(4);
  const [theatreTonnage, setTheatreTonnage] = useState(8);
  const [zeroGRoomTonnage, setZeroGRoomTonnage] = useState(1);
  
  const allSystems = OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "commonArea");
  
  // Define grouped system IDs
  const SWIMMING_POOL_IDS = ["swimmingPool", "endlessPool"];
  const THEATRE_IDS = ["theatreBasic", "theatreAdvanced"];
  
  // Get grouped system options
  const swimmingPoolOptions = allSystems.filter(s => SWIMMING_POOL_IDS.includes(s.subtype));
  const selectedSwimmingPoolObj = swimmingPoolOptions.find(s => s.subtype === selectedSwimmingPool);
  
  const theatreOptions = allSystems.filter(s => THEATRE_IDS.includes(s.subtype));
  const selectedTheatreObj = theatreOptions.find(s => s.subtype === selectedTheatre);
  
  // Filter out grouped systems from the main list
  const individualSystems = allSystems.filter(s => 
    !SWIMMING_POOL_IDS.includes(s.subtype) &&
    !THEATRE_IDS.includes(s.subtype)
  );
  
  const handleAddCommonAreaSystem = (system: any) => {
    let calculatedCost = system.cost;
    let calculatedTonnage = system.tonnage;
    let calculatedPower = system.power || 0;
    
    // Calculate stats based on system type
    switch (system.subtype) {
      case "advancedEntertainment":
        calculatedCost = entertainmentCost;
        calculatedTonnage = 0;
        break;
      case "brewery":
        calculatedTonnage = breweryTonnage;
        calculatedCost = breweryTonnage * 100000; // Cr100,000 per ton
        break;
      case "gourmetKitchen":
        calculatedTonnage = gourmetDiners;
        calculatedCost = gourmetDiners * 200000; // Cr200,000 per diner
        break;
      case "hotTub":
        calculatedTonnage = hotTubUsers * 0.25;
        calculatedCost = hotTubUsers * 0.25 * 12000; // Cr12,000 per ton
        break;
      case "swimmingPool":
        calculatedTonnage = swimmingPoolTonnage;
        calculatedCost = swimmingPoolTonnage * 20000; // Cr20,000 per ton
        break;
      case "endlessPool":
        calculatedTonnage = swimmingPoolTonnage;
        calculatedCost = swimmingPoolTonnage * 50000; // Cr50,000 per ton
        break;
      case "theatreBasic":
        calculatedTonnage = theatreTonnage;
        calculatedCost = theatreTonnage * 100000; // Cr100,000 per ton
        break;
      case "theatreAdvanced":
        calculatedTonnage = theatreTonnage;
        calculatedCost = theatreTonnage * 200000; // Cr200,000 per ton
        break;
      case "zeroGRoom":
        calculatedTonnage = zeroGRoomTonnage;
        calculatedCost = zeroGRoomTonnage * 50000; // Cr50,000 per ton
        break;
      default:
        // For other systems, use base stats
        calculatedCost = system.cost;
        calculatedTonnage = system.tonnage;
    }
    
    const finalSystem = {
      ...system,
      cost: calculatedCost,
      tonnage: calculatedTonnage,
      power: calculatedPower,
      properties: {
        ...system.properties,
        entertainmentTechLevel,
        entertainmentCost,
        breweryTonnage,
        gourmetDiners,
        hotTubUsers,
        swimmingPoolTonnage,
        theatreTonnage,
        zeroGRoomTonnage,
      },
      id: `${system.id}_${Date.now()}`, // Ensure unique ID
    };
    
    dispatch(addModuleSystem({
      systemType: "optionalSystems",
      system: finalSystem,
    }));
  };
  
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString();
  };
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Common Areas Systems</h3>
      
      {/* Grouped Systems */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Swimming Pool Card with Dropdown */}
        {swimmingPoolOptions.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Swimming Pool</div>
              <select
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm mb-2 dark:bg-gray-600 dark:text-gray-100"
                value={selectedSwimmingPool}
                onChange={(e) => setSelectedSwimmingPool(e.target.value)}
              >
                {swimmingPoolOptions.map((opt) => (
                  <option key={opt.subtype} value={opt.subtype}>
                    {opt.name}
                  </option>
                ))}
              </select>
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Pool Size (tons)
                </label>
                <input
                  type="number"
                  min={selectedSwimmingPoolObj?.properties?.minTonnage || 1}
                  value={swimmingPoolTonnage}
                  onChange={(e) => setSwimmingPoolTonnage(Math.max(selectedSwimmingPoolObj?.properties?.minTonnage || 1, Number(e.target.value)))}
                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                />
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <div><b>Cost:</b> {formatCurrency(selectedSwimmingPool === "swimmingPool" ? swimmingPoolTonnage * 20000 : swimmingPoolTonnage * 50000)} Cr</div>
                <div><b>Tonnage:</b> {swimmingPoolTonnage} tons</div>
                <div><b>Power:</b> 0 Power</div>
              </div>
            </div>
            <button
              className="px-4 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
              onClick={() => selectedSwimmingPoolObj && handleAddCommonAreaSystem(selectedSwimmingPoolObj)}
            >
              Add
            </button>
          </div>
        )}
        
        {/* Theatre Card with Dropdown */}
        {theatreOptions.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Theatre</div>
              <select
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm mb-2 dark:bg-gray-600 dark:text-gray-100"
                value={selectedTheatre}
                onChange={(e) => setSelectedTheatre(e.target.value)}
              >
                {theatreOptions.map((opt) => (
                  <option key={opt.subtype} value={opt.subtype}>
                    {opt.name}
                  </option>
                ))}
              </select>
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Theatre Size (tons)
                </label>
                <input
                  type="number"
                  min={selectedTheatreObj?.properties?.minTonnage || 8}
                  value={theatreTonnage}
                  onChange={(e) => setTheatreTonnage(Math.max(selectedTheatreObj?.properties?.minTonnage || 8, Number(e.target.value)))}
                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                />
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <div><b>Cost:</b> {formatCurrency(selectedTheatre === "theatreBasic" ? theatreTonnage * 100000 : theatreTonnage * 200000)} Cr</div>
                <div><b>Tonnage:</b> {theatreTonnage} tons</div>
                <div><b>Power:</b> 0 Power</div>
              </div>
            </div>
            <button
              className="px-4 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
              onClick={() => selectedTheatreObj && handleAddCommonAreaSystem(selectedTheatreObj)}
            >
              Add
            </button>
          </div>
        )}
      </div>
      
      {/* Individual Systems */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {individualSystems.map((system) => {
          const isInstalled = installedSystems.some(s => s.id.startsWith(system.id + '_'));
          const isOverTechLevel = system.techLevel > moduleConfig.techLevel;
          const totalTons = installedSystems.reduce((sum, sys) => sum + (sys.tonnage || 0), 0);
          
          // Calculate display values
          let displayCost = system.cost;
          let displayTonnage = system.tonnage;
          let displayPower = system.power || 0;
          
          // Special handling for different system types
          switch (system.subtype) {
            case "advancedEntertainment":
              displayCost = entertainmentCost;
              displayTonnage = 0;
              break;
            case "brewery":
              displayTonnage = breweryTonnage;
              displayCost = breweryTonnage * 100000;
              break;
            case "gourmetKitchen":
              displayTonnage = gourmetDiners;
              displayCost = gourmetDiners * 200000;
              break;
            case "hotTub":
              displayTonnage = hotTubUsers * 0.25;
              displayCost = hotTubUsers * 0.25 * 12000;
              break;
            case "zeroGRoom":
              displayTonnage = zeroGRoomTonnage;
              displayCost = zeroGRoomTonnage * 50000;
              break;
            default:
              // For other systems, use base stats
              displayCost = system.cost;
              displayTonnage = system.tonnage;
          }
          
          const wouldExceedTonnage = (totalTons + displayTonnage) > moduleConfig.size;
          const isDisabled = isInstalled || isOverTechLevel || wouldExceedTonnage;
          
          return (
            <div
              key={system.id}
              className={`border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 ${
                isDisabled ? "opacity-60" : ""
              }`}
            >
              <div>
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{system.name}</h4>
                  <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                    TL{system.techLevel}
                  </span>
                </div>
                
                {/* Custom input fields for specific systems */}
                {system.subtype === "advancedEntertainment" && (
                  <div className="mb-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tech Level
                      </label>
                      <input
                        type="number"
                        min={5}
                        max={15}
                        value={entertainmentTechLevel}
                        onChange={(e) => setEntertainmentTechLevel(Math.max(5, Math.min(15, Number(e.target.value))))}
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                        disabled={isInstalled}
                      />
                    </div>
                    <div className="mt-2">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Cost (Cr)
                      </label>
                      <input
                        type="number"
                        min={100}
                        max={10000}
                        value={entertainmentCost}
                        onChange={(e) => setEntertainmentCost(Math.max(100, Math.min(10000, Number(e.target.value))))}
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                        disabled={isInstalled}
                      />
                    </div>
                  </div>
                )}
                
                {system.subtype === "brewery" && (
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Brewery Size (tons)
                    </label>
                    <input
                      type="number"
                      min={0.5}
                      step={0.1}
                      value={breweryTonnage}
                      onChange={(e) => setBreweryTonnage(Math.max(0.5, Number(e.target.value)))}
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                      disabled={isInstalled}
                    />
                  </div>
                )}
                
                {system.subtype === "gourmetKitchen" && (
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Number of Diners
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={gourmetDiners}
                      onChange={(e) => setGourmetDiners(Math.max(1, Number(e.target.value)))}
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                      disabled={isInstalled}
                    />
                  </div>
                )}
                
                {system.subtype === "hotTub" && (
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Number of Users
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={hotTubUsers}
                      onChange={(e) => setHotTubUsers(Math.max(1, Number(e.target.value)))}
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                      disabled={isInstalled}
                    />
                  </div>
                )}
                
                {system.subtype === "zeroGRoom" && (
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Room Size (tons)
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={zeroGRoomTonnage}
                      onChange={(e) => setZeroGRoomTonnage(Math.max(1, Number(e.target.value)))}
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                      disabled={isInstalled}
                    />
                  </div>
                )}
                
                <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <div><b>Cost:</b> {formatCurrency(displayCost)} Cr</div>
                  <div><b>Tonnage:</b> {displayTonnage.toFixed(1)} tons</div>
                  <div><b>Power:</b> {displayPower} Power</div>
                </div>
              </div>
              <div className="mt-2">
                {!isInstalled ? (
                  <button
                    className={`w-full mt-2 px-3 py-1 rounded text-white font-semibold ${
                      isDisabled
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                    onClick={() => handleAddCommonAreaSystem(system)}
                    disabled={isDisabled}
                    title={
                      isInstalled
                        ? "Already installed"
                        : isOverTechLevel
                        ? `Requires TL ${system.techLevel}`
                        : wouldExceedTonnage
                        ? "Would exceed module tonnage"
                        : "Add system"
                    }
                  >
                    Add
                  </button>
                ) : (
                  <button
                    className="px-4 py-1 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                    onClick={() => {
                      dispatch(removeModuleSystem({
                        systemType: "optionalSystems",
                        systemId: system.id,
                      }));
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Wrapper component for Drone Systems
 */
const DroneSystemsWrapper: React.FC<{ moduleConfig: any }> = ({ moduleConfig }) => {
  const dispatch = useAppDispatch();
  const installedSystems = useAppSelector((state) => state.module.moduleConfig?.systems.optionalSystems || []);
  
  // State for drone quantities
  const [quantities, setQuantities] = useState<{ [id: string]: number }>({
    miningDrone: 5,
    probeDrone: 5,
    advancedProbeDrone: 5,
    repairDrone: 1,
  });
  
  // const allSystems = OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "drone");
  
  // Define drone types with their specifications
  const DRONE_TYPES = [
    {
      id: "miningDrone",
      label: "Mining Drone",
      subtype: "mining",
      defaultQty: 5,
      description: "",
      costPerSet: 1000000,
      tonnagePerSet: 10,
      costPerTon: 0,
      tonnagePercent: 0,
    },
    {
      id: "probeDrone",
      label: "Probe Drone",
      subtype: "probe",
      defaultQty: 5,
      description: "",
      costPerSet: 500000,
      tonnagePerSet: 1,
      costPerTon: 0,
      tonnagePercent: 0,
    },
    {
      id: "advancedProbeDrone",
      label: "Advanced Probe Drone",
      subtype: "advancedProbe",
      defaultQty: 5,
      description: "",
      costPerSet: 800000,
      tonnagePerSet: 1,
      costPerTon: 0,
      tonnagePercent: 0,
    },
    {
      id: "repairDrone",
      label: "Repair Drone",
      subtype: "repair",
      defaultQty: 1,
      description: "",
      costPerSet: 0,
      tonnagePerSet: 0,
      costPerTon: 200000,
      tonnagePercent: 0.01, // 1%
    },
  ];
  
  const handleAddDrone = (droneType: any) => {
    const inputValue = quantities[droneType.id] || droneType.defaultQty;
    let calculatedCost = 0;
    let calculatedTonnage = 0;
    
    // Calculate stats based on drone type
    if (droneType.id === "repairDrone") {
      // Repair drones are calculated based on module size
      calculatedTonnage = Math.max(1, Math.ceil(moduleConfig.size * droneType.tonnagePercent));
      calculatedCost = calculatedTonnage * droneType.costPerTon;
    } else {
      // Other drones are calculated based on quantity
      const sets = Math.ceil(inputValue / 5); // 5 drones per set
      calculatedTonnage = sets * droneType.tonnagePerSet;
      calculatedCost = sets * droneType.costPerSet;
    }
    
    // Add multiple drones based on the quantity input
    for (let i = 0; i < inputValue; i++) {
      const system = {
        id: `drone-${Date.now()}-${i}`,
        type: "drone",
        subtype: droneType.subtype,
        name: droneType.label,
        description: droneType.description,
        cost: calculatedCost / inputValue, // Distribute cost across individual drones
        tonnage: calculatedTonnage / inputValue, // Distribute tonnage across individual drones
        power: 0, // Drones don't use power
        techLevel: 8, // Default TL for drones
        properties: {
          quantity: 1, // Each individual drone has quantity 1
          droneType: droneType.id,
        },
      };
      
      dispatch(addModuleSystem({
        systemType: "optionalSystems",
        system,
      }));
    }
  };
  
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString();
  };
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Drone Systems</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {DRONE_TYPES.map((droneType) => {
          const qty = quantities[droneType.id];
          const totalTons = installedSystems.reduce((sum, sys) => sum + (sys.tonnage || 0), 0);
          
          // Calculate stats based on drone type
          let calculatedCost = 0;
          let calculatedTonnage = 0;
          let note = "";
          
          if (droneType.id === "repairDrone") {
            calculatedTonnage = Math.max(1, Math.ceil(moduleConfig.size * droneType.tonnagePercent));
            calculatedCost = calculatedTonnage * droneType.costPerTon;
            note = `Based on module size (${moduleConfig.size} tons)`;
          } else {
            const sets = Math.ceil(qty / 5); // 5 drones per set
            calculatedTonnage = sets * droneType.tonnagePerSet;
            calculatedCost = sets * droneType.costPerSet;
          }
          
          const wouldExceedTonnage = (totalTons + calculatedTonnage) > moduleConfig.size;
          const isDisabled = wouldExceedTonnage;
          
          return (
            <div
              key={droneType.id}
              className={`border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 ${
                isDisabled ? "opacity-60" : ""
              }`}
            >
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  {droneType.label}
                </div>
                <div className="text-gray-700 dark:text-gray-300 text-sm mb-2">
                  {droneType.description}
                </div>
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={qty}
                    onChange={(e) =>
                      setQuantities({
                        ...quantities,
                        [droneType.id]: Math.max(
                          1,
                          Math.floor(Number(e.target.value)),
                        ),
                      })
                    }
                    className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                    disabled={isDisabled}
                  />
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <div>
                    <b>Cost:</b> {formatCurrency(calculatedCost)} Cr
                  </div>
                  <div>
                    <b>Tonnage:</b> {calculatedTonnage} tons
                  </div>
                  <div>
                    <b>Power:</b> 0 Power
                  </div>
                </div>
                {note && (
                  <div className="text-xs text-blue-700 dark:text-blue-300 mb-2">
                    {note}
                  </div>
                )}
                {isDisabled && (
                  <div className="text-xs text-red-600 dark:text-red-400 mb-2">
                    Would exceed module tonnage
                  </div>
                )}
              </div>
              <button
                className={`px-4 py-1 rounded text-white font-semibold ${
                  isDisabled 
                    ? "bg-gray-400 cursor-not-allowed" 
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
                onClick={() => handleAddDrone(droneType)}
                disabled={isDisabled}
                title={
                  isDisabled
                    ? "Would exceed module tonnage"
                    : "Add drone system"
                }
              >
                Add Drone
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Simplified MES Inline Editor for Module Builder
 */
const MESInlineEditor: React.FC<{
  availableSystems: any[];
  shipBuilderState: any;
  onConfirm: (updatedSystem: any) => void;
  onCancel: () => void;
  editingSystem?: any;
}> = ({ availableSystems, shipBuilderState, onConfirm, onCancel, editingSystem }) => {
  const [multiEnvSelections, setMultiEnvSelections] = useState<{
    [id: string]: { selected: boolean; tons: number; environmentType: string };
  }>({});
  const [customSelected, setCustomSelected] = useState(false);
  const [customTons, setCustomTons] = useState(0);
  const [customEnvType, setCustomEnvType] = useState("");

  // Module-specific ship spaces
  const MODULE_SHIP_SPACES = [
    { id: "bridge", label: "Bridge" },
    { id: "computer", label: "Computer" },
    { id: "sensors", label: "Sensors" },
    { id: "powerPlant", label: "Power Plant" },
    { id: "maneuverDrive", label: "Maneuver Drive" },
    { id: "jumpDrive", label: "Jump Drive" },
    { id: "reactionDrive", label: "Reaction Drive" },
    { id: "fuelTanks", label: "Fuel Tanks" },
    { id: "staterooms", label: "Staterooms" },
    { id: "cargo", label: "Cargo" },
    { id: "weapons", label: "Weapons" },
    { id: "hangar", label: "Hangar Bay" },
    { id: "medicalBay", label: "Medical Bay" },
    { id: "droneBay", label: "Drone Bay" },
    { id: "externalSystems", label: "External Systems" },
    { id: "internalSystems", label: "Internal Systems" },
  ];

  // Pre-fill selections when editing
  useEffect(() => {
    if (editingSystem && editingSystem.coveredSpaces) {
      const selections: {
        [id: string]: {
          selected: boolean;
          tons: number;
          environmentType: string;
        };
      } = {};
      editingSystem.coveredSpaces.forEach((space: any) => {
        if (space.id === "custom") {
          setCustomSelected(true);
          setCustomTons(space.tons);
          setCustomEnvType(space.environmentType);
        } else {
          selections[space.id] = {
            selected: true,
            tons: space.tons,
            environmentType: space.environmentType,
          };
        }
      });
      setMultiEnvSelections(selections);
    } else {
      setMultiEnvSelections({});
      setCustomSelected(false);
      setCustomTons(0);
      setCustomEnvType("");
    }
  }, [editingSystem]);

  const hasValidArea = Object.entries(multiEnvSelections).some(
    ([id, v]) =>
      v.selected &&
      typeof v.environmentType === "string" &&
      v.environmentType.trim() !== "" &&
      Number(v.tons) > 0,
  );
  const hasValidCustom =
    customSelected && customEnvType.trim() !== "" && Number(customTons) > 0;

  // Calculate stats
  const totalTons = [
    ...Object.entries(multiEnvSelections)
      .filter(([id, v]) => v.selected && v.environmentType && Number(v.tons) > 0)
      .map(([id, v]) => Number(v.tons)),
    ...(hasValidCustom ? [Number(customTons)] : []),
  ].reduce((sum, tons) => sum + tons, 0);

  const equipmentTonnage = totalTons * 0.05; // 5% for equipment
  const totalCost = totalTons * 100000; // Cr100,000 per ton
  const totalPower = 5; // Fixed 5 power

  const handleConfirm = () => {
    const base = availableSystems.find(
      (s) => s.subtype === "multiEnvironmentSpace",
    );
    if (!base) return;

    // Gather all selected areas
    const coveredSpacesData = [
      ...Object.entries(multiEnvSelections)
        .filter(
          ([id, v]) => v.selected && v.environmentType && Number(v.tons) > 0,
        )
        .map(([id, v]) => {
          const area = MODULE_SHIP_SPACES.find((s) => s.id === id);
          return {
            id,
            label: area ? area.label : id,
            tons: Number(v.tons),
            environmentType: v.environmentType,
          };
        }),
      ...(hasValidCustom
        ? [
            {
              id: "custom",
              label: "Custom Area",
              tons: Number(customTons),
              environmentType: customEnvType,
            },
          ]
        : []),
    ];

    const updatedSystem = {
      ...(editingSystem || base),
      id: editingSystem?.id || `multiEnv-${Date.now()}`,
      tonnage: totalTons + equipmentTonnage,
      cost: totalCost,
      power: totalPower,
      properties: {
        ...base.properties,
        tonnage: totalTons + equipmentTonnage,
        equipmentTonnage: equipmentTonnage,
      },
      coveredSpaces: coveredSpacesData,
    };

    onConfirm(updatedSystem);
  };

  return (
    <div className="p-4 border rounded-lg bg-gray-50">
      <h4 className="font-medium text-gray-900 mb-3">
        {editingSystem
          ? "Edit Multi-Environment Space"
          : "Add Multi-Environment Space"}
      </h4>

      {/* Custom Area - Designated Area */}
      <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <input
            type="checkbox"
            checked={customSelected}
            onChange={(e) => setCustomSelected(e.target.checked)}
            className="w-5 h-5"
          />
          <span className="text-sm font-semibold text-blue-800 bg-blue-100 px-2 py-1 rounded">
            DESIGNATED AREA
          </span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-blue-800 font-medium">
            Size in Tons:
          </label>
          <input
            type="number"
            min={0}
            step={0.1}
            value={customTons}
            onChange={(e) => setCustomTons(Number(e.target.value))}
            className="w-24 px-2 py-1 border rounded bg-white"
            placeholder="Tons"
            disabled={!customSelected}
          />
          <input
            type="text"
            placeholder="Environment Type"
            value={customEnvType}
            onChange={(e) => setCustomEnvType(e.target.value)}
            className="w-48 px-2 py-1 border rounded bg-white"
            disabled={!customSelected}
          />
        </div>
      </div>

      {/* Module Areas Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
        {MODULE_SHIP_SPACES.map((space) => {
          const sel = multiEnvSelections[space.id] || {
            selected: false,
            tons: 0,
            environmentType: "",
          };
          return (
            <div
              key={space.id}
              className="flex items-center gap-2 border-b py-1"
            >
              <input
                type="checkbox"
                checked={sel.selected}
                onChange={(e) =>
                  setMultiEnvSelections((s) => ({
                    ...s,
                    [space.id]: {
                      ...sel,
                      selected: e.target.checked,
                      tons: sel.tons || 0,
                    },
                  }))
                }
              />
              <span className="w-32">
                {space.label}
              </span>
              {sel.selected && (
                <>
                  <input
                    type="number"
                    placeholder="Tons"
                    value={sel.tons}
                    onChange={(e) =>
                      setMultiEnvSelections((s) => ({
                        ...s,
                        [space.id]: { ...sel, tons: Number(e.target.value) },
                      }))
                    }
                    className="w-24 px-2 py-1 border rounded bg-gray-100 text-gray-600 text-center"
                    min="0"
                    step="0.1"
                  />
                  <input
                    type="text"
                    placeholder="Environment Type"
                    value={sel.environmentType}
                    onChange={(e) =>
                      setMultiEnvSelections((s) => ({
                        ...s,
                        [space.id]: { ...sel, environmentType: e.target.value },
                      }))
                    }
                    className="w-32 px-2 py-1 border rounded"
                  />
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Live Summary */}
      <div className="mb-4 p-2 bg-gray-100 rounded text-sm">
        <b>Summary:</b> Total Space: {totalTons.toFixed(1)} tons | Equipment: +
        {equipmentTonnage.toFixed(1)} tons | Cost: {totalCost.toLocaleString()} Cr | Power: {totalPower}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <button
          className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          disabled={!hasValidArea && !hasValidCustom}
          onClick={handleConfirm}
        >
          {editingSystem ? "Update" : "Add"}
        </button>
      </div>
    </div>
  );
};

/**
 * Module Systems Step Component
 * Handles adding optional systems and equipment to the module
 */
export const ModuleSystemsStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const moduleConfig = useAppSelector((state) => state.module.moduleConfig);
  const [activeTab, setActiveTab] = useState(0);

  if (!moduleConfig) {
    return <div className="text-gray-600 dark:text-gray-300">Loading module configuration...</div>;
  }

  const installedSystems = moduleConfig.systems.optionalSystems || [];

  // Create tab configuration with wrapper components
  // Note: Structure and Bridge systems are not allowed in modular hulls
  // Power systems and drives ARE allowed in modular hulls (based on Traveller precedent)
  const tabs = [
    { 
      label: "Power", 
      component: PowerSystemsWrapper,
      count: OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "power").length
    },
    { 
      label: "Drives", 
      component: DriveSystemsWrapper,
      count: OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "drive").length
    },
    { 
      label: "Fuel", 
      component: FuelSystemsWrapper,
      count: OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "fuel").length
    },
    { 
      label: "Accommodations", 
      component: AccommodationSystemsWrapper,
      count: OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "accommodation").length
    },
    { 
      label: "Cargo", 
      component: CargoSystemsWrapper,
      count: OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "cargo").length
    },
    { 
      label: "External", 
      component: ExternalSystemsWrapper,
      count: OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "external").length
    },
    { 
      label: "Internal", 
      component: InternalSystemsWrapper,
      count: OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "internal").length
    },
    { 
      label: "Sensors", 
      component: SensorSystemsWrapper,
      count: OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "sensor").length
    },
    { 
      label: "Drones", 
      component: DroneSystemsWrapper,
      count: OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "drone").length
    },
    { 
      label: "Common Areas", 
      component: CommonAreasSystemsWrapper,
      count: OPTIONAL_SYSTEM_TYPES.filter(system => system.type === "commonArea").length
    },
  ];

  // Calculate totals
  const totalCost = installedSystems.reduce((sum, sys) => sum + (sys.cost || 0), 0);
  const totalTons = installedSystems.reduce((sum, sys) => sum + (sys.tonnage || 0), 0);
  const totalPower = installedSystems.reduce((sum, sys) => sum + (sys.power || 0), 0);
  const remainingTons = moduleConfig.size - totalTons;
  const baseModuleCost = calculateModuleCost(moduleConfig.size);

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString();
  };

  const currentTab = tabs[activeTab];
  const TabComponent = currentTab?.component;

  return (
    <div className="space-y-6">


      {/* Module Capacity Summary */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-3">📊 Module Capacity Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="font-medium text-blue-700 dark:text-blue-300">Module Size:</span>
            <div className="text-blue-900 dark:text-blue-100">{moduleConfig.size} tons</div>
          </div>
          <div>
            <span className="font-medium text-blue-700 dark:text-blue-300">Used Tonnage:</span>
            <div className="text-blue-900 dark:text-blue-100">{totalTons} tons</div>
          </div>
          <div>
            <span className="font-medium text-blue-700 dark:text-blue-300">Remaining:</span>
            <div className={`font-semibold ${remainingTons < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
              {remainingTons} tons
            </div>
          </div>
          <div>
            <span className="font-medium text-blue-700 dark:text-blue-300">Power Used:</span>
            <div className="text-blue-900 dark:text-blue-100">{totalPower} Power</div>
          </div>
        </div>
      </div>

      {/* Cost Summary */}
      <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-3">💰 Cost Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="font-medium text-green-700 dark:text-green-300">Base Module Cost:</span>
            <div className="text-green-900 dark:text-green-100">{formatCurrency(baseModuleCost)} Cr</div>
          </div>
          <div>
            <span className="font-medium text-green-700 dark:text-green-300">Systems Cost:</span>
            <div className="text-green-900 dark:text-green-100">{formatCurrency(totalCost)} Cr</div>
          </div>
          <div>
            <span className="font-medium text-green-700 dark:text-green-300">Total Module Cost:</span>
            <div className="text-green-900 dark:text-green-100 font-semibold">{formatCurrency(baseModuleCost + totalCost)} Cr</div>
          </div>
        </div>
      </div>



      {/* Tabbed System Selection */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 rounded-lg">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">Add Optional Systems</h3>
          
          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab, index) => (
              <button
                key={tab.label}
                onClick={() => setActiveTab(index)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === index
                    ? "bg-highlightOrange text-black shadow-md"
                    : "bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-400 dark:hover:bg-gray-500"
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
          {TabComponent ? (
            <TabComponent moduleConfig={moduleConfig} />
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No systems available in {currentTab?.label} category.
            </div>
          )}
        </div>
      </div>

      {/* Current Systems */}
      {installedSystems.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">🔧 Installed Systems ({installedSystems.length})</h3>
          <div className="space-y-2">
            {installedSystems.map((system, index) => (
              <div key={system.id || index} className="flex items-center justify-between p-3 bg-white rounded border">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{system.name}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Cost: {formatCurrency(system.cost || 0)} Cr | 
                    Tons: {system.tonnage || 0} | 
                    Power: {system.power || 0} | 
                    TL: {system.techLevel || 0}
                  </div>
                </div>
                <button
                  onClick={() => {
                    dispatch(removeModuleSystem({
                      systemType: "optionalSystems",
                      systemId: system.id || `system-${index}`,
                    }));
                  }}
                  className="ml-4 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Configuration Components for External Systems
interface ConfigProps {
  system: any;
  onConfigure: (system: any, configData?: any) => void;
  onCancel: () => void;
}

const DockingClampConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [clampType, setClampType] = useState("typeI");

  const clampTypes = [
    {
      value: "typeI",
      label: "Type I (1-30 tons)",
      maxTonnage: 30,
      cost: 50000,
      tonnage: 1,
    },
    {
      value: "typeII",
      label: "Type II (31-99 tons)",
      maxTonnage: 99,
      cost: 100000,
      tonnage: 2,
    },
    {
      value: "typeIII",
      label: "Type III (100-300 tons)",
      maxTonnage: 300,
      cost: 200000,
      tonnage: 3,
    },
    {
      value: "typeIV",
      label: "Type IV (301-2,000 tons)",
      maxTonnage: 2000,
      cost: 500000,
      tonnage: 5,
    },
    {
      value: "typeV",
      label: "Type V (2,001+ tons)",
      maxTonnage: 999999,
      cost: 1000000,
      tonnage: 10,
    },
  ];

  const selectedClamp = clampTypes.find((c) => c.value === clampType);

  const handleConfigure = () => {
    onConfigure(system, { clampType });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Clamp Type
        </label>
        <select
          value={clampType}
          onChange={(e) => setClampType(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        >
          {clampTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label} - {type.cost.toLocaleString()}, {type.tonnage} tons
            </option>
          ))}
        </select>
      </div>
      {selectedClamp && (
        <div className="text-sm text-gray-600">
          <p>Cost: {selectedClamp.cost.toLocaleString()} Cr</p>
          <p>Tonnage: {selectedClamp.tonnage} tons</p>
          <p>Max attached ship: {selectedClamp.maxTonnage} tons</p>
        </div>
      )}
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const ForcedLinkageConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [linkageType, setLinkageType] = useState("basic");

  const linkageTypes = [
    {
      value: "basic",
      label: "Basic (TL7)",
      pilotDM: -2,
      cost: 100000,
      tonnage: 2,
    },
    {
      value: "improved",
      label: "Improved (TL9)",
      pilotDM: -1,
      cost: 200000,
      tonnage: 2,
    },
    {
      value: "enhanced",
      label: "Enhanced (TL12)",
      pilotDM: 0,
      cost: 500000,
      tonnage: 2,
    },
    {
      value: "advanced",
      label: "Advanced (TL15)",
      pilotDM: 2,
      cost: 1000000,
      tonnage: 2,
    },
  ];

  const selectedLinkage = linkageTypes.find((l) => l.value === linkageType);

  const handleConfigure = () => {
    onConfigure(system, { linkageType });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Linkage Type
        </label>
        <select
          value={linkageType}
          onChange={(e) => setLinkageType(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        >
          {linkageTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label} - {type.cost.toLocaleString()}, Pilot DM{" "}
              {type.pilotDM >= 0 ? "+" : ""}
              {type.pilotDM}
            </option>
          ))}
        </select>
      </div>
      {selectedLinkage && (
        <div className="text-sm text-gray-600">
          <p>Cost: {selectedLinkage.cost.toLocaleString()} Cr</p>
          <p>
            Pilot DM: {selectedLinkage.pilotDM >= 0 ? "+" : ""}
            {selectedLinkage.pilotDM}
          </p>
          <p>Tonnage: {selectedLinkage.tonnage} tons</p>
          <p>Max target: 5,000 tons</p>
        </div>
      )}
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const GrapplingArmConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [armType, setArmType] = useState("standard");

  const armTypes = [
    {
      value: "standard",
      label: "Standard Grappling Arm",
      maxWeight: 2,
      cost: 50000,
      tonnage: 1,
    },
    {
      value: "heavy",
      label: "Heavy Grappling Arm",
      maxWeight: 10,
      cost: 200000,
      tonnage: 3,
    },
  ];

  const selectedArm = armTypes.find((a) => a.value === armType);

  const handleConfigure = () => {
    onConfigure(system, { armType });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Arm Type
        </label>
        <select
          value={armType}
          onChange={(e) => setArmType(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        >
          {armTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label} - {type.cost.toLocaleString()}, {type.tonnage} tons,{" "}
              {type.maxWeight} tons max
            </option>
          ))}
        </select>
      </div>
      {selectedArm && (
        <div className="text-sm text-gray-600">
          <p>Cost: {selectedArm.cost.toLocaleString()} Cr</p>
          <p>Tonnage: {selectedArm.tonnage} tons</p>
          <p>Max weight: {selectedArm.maxWeight} tons</p>
          <p>Reach: 250 metres</p>
        </div>
      )}
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// Configuration Components for Internal Systems
interface InternalConfigProps {
  system: any;
  onConfigure: (system: any, configData?: any) => void;
  onCancel: () => void;
  moduleConfig?: any;
  installedSystems?: any[];
}

const AirlockConfig: React.FC<InternalConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [tonnage, setTonnage] = useState<number>(1);
  
  const handleConfigure = () => {
    onConfigure(system, { tonnage });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Size (tons)</label>
        <input
          type="number"
          min={2}
          value={tonnage}
          onChange={(e) => setTonnage(Math.max(2, Number(e.target.value)))}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div className="text-sm text-gray-600">
        <p>Cost: {(tonnage * 50000).toLocaleString()} Cr</p>
        <p>Tonnage: {tonnage} tons</p>
        <p>Power: 0 Power</p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const ArmouryConfig: React.FC<InternalConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [tonnage, setTonnage] = useState<number>(1);
  
  const handleConfigure = () => {
    onConfigure(system, { tonnage });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tonnage</label>
        <input
          type="number"
          min={1}
          value={tonnage}
          onChange={(e) => setTonnage(Math.max(1, Number(e.target.value)))}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div className="text-sm text-gray-600">
        <p>Cost: {(tonnage * 250000).toLocaleString()} Cr</p>
        <p>Tonnage: {tonnage} tons</p>
        <p>Power: 0 Power</p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const BiosphereConfig: React.FC<InternalConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [tonnage, setTonnage] = useState<number>(1);
  
  const handleConfigure = () => {
    onConfigure(system, { tonnage });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tonnage</label>
        <input
          type="number"
          min={1}
          value={tonnage}
          onChange={(e) => setTonnage(Math.max(1, Number(e.target.value)))}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div className="text-sm text-gray-600">
        <p>Cost: {(tonnage * 1000000).toLocaleString()} Cr</p>
        <p>Tonnage: {tonnage} tons</p>
        <p>Power: {tonnage * 2} Power</p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const BoobyTrapConfig: React.FC<InternalConfigProps> = ({
  system,
  onConfigure,
  onCancel,
  moduleConfig,
  installedSystems = [],
}) => {
  const [trappedCount, setTrappedCount] = useState<number>(1);
  const [boobyTrapType, setBoobyTrapType] = useState<string>("basic");
  
  // Calculate total airlocks (1 per 100 tons + additional airlocks)
  const additionalAirlocks = installedSystems.filter((s: any) => s.subtype === "internalAirlock").length;
  const totalAirlocks = Math.ceil((moduleConfig?.size || 0) / 100) + additionalAirlocks;
  
  const trapTypes = [
    { value: "basic", label: "Basic", cost: 100000, damage: "3D" },
    { value: "improved", label: "Improved", cost: 200000, damage: "4D" },
    { value: "advanced", label: "Advanced", cost: 500000, damage: "5D" },
  ];
  
  const selectedTrap = trapTypes.find(t => t.value === boobyTrapType) || trapTypes[0];
  const totalCost = selectedTrap.cost * trappedCount;
  
  const handleConfigure = () => {
    onConfigure(system, { trappedCount, boobyTrapType });
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-700 mb-2">
        This module has <b>{totalAirlocks}</b> airlock(s) (1 per 100 tons, plus any additional airlocks).
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Number of airlocks to trap
        </label>
        <input
          type="number"
          min={1}
          max={totalAirlocks}
          value={trappedCount}
          onChange={(e) => setTrappedCount(Math.max(1, Math.min(totalAirlocks, Math.floor(Number(e.target.value)))))}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Trap Type</label>
        <select
          value={boobyTrapType}
          onChange={(e) => setBoobyTrapType(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        >
          {trapTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label} ({type.cost.toLocaleString()} Cr, {type.damage} dmg)
            </option>
          ))}
        </select>
      </div>
      <div className="text-sm text-gray-600">
        <p>Cost: {totalCost.toLocaleString()} Cr</p>
        <p>Effect: Deals {selectedTrap.damage} per round per trapped airlock ({trappedCount} total)</p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const ConcealedCompartmentConfig: React.FC<InternalConfigProps> = ({
  system,
  onConfigure,
  onCancel,
  moduleConfig,
}) => {
  const [tonnage, setTonnage] = useState<number>(1);
  
  const handleConfigure = () => {
    onConfigure(system, { tonnage });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tonnage</label>
        <input
          type="number"
          min={1}
          max={moduleConfig?.size || 999}
          value={tonnage}
          onChange={(e) => setTonnage(Math.max(1, Math.min(moduleConfig?.size || 999, Number(e.target.value))))}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div className="text-sm text-gray-600">
        <p>Cost: {(tonnage * 500000).toLocaleString()} Cr</p>
        <p>Tonnage: {tonnage} tons</p>
        <p>Power: 0 Power</p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const DockingSpaceConfig: React.FC<InternalConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [vesselSize, setVesselSize] = useState<number>(1);
  
  // Calculate required docking space tonnage: vessel size + 10%
  // Fix floating-point precision issue: use integer math to avoid 100 * 1.1 = 110.00000000000001
  const requiredDockingSpace = Math.ceil((vesselSize * 11) / 10);
  const cost = requiredDockingSpace * 250000; // MCr0.25 per ton
  
  const handleConfigure = () => {
    onConfigure(system, { 
      tonnage: requiredDockingSpace,
      vesselCapacity: vesselSize // Store original vessel size for capacity calculation
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Vehicle/Vessel Size (tons)
        </label>
        <input
          type="number"
          min={1}
          value={vesselSize}
          onChange={(e) => setVesselSize(Math.max(1, Math.floor(Number(e.target.value))))}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="text-sm text-gray-600 mt-1">
          Docking space required: {requiredDockingSpace} tons (vessel size + 10% for maneuvering room)
        </p>
      </div>
      <div className="text-sm text-gray-600">
        <p>Cost: {cost.toLocaleString()} Cr</p>
        <p>Space Required: {requiredDockingSpace} tons</p>
        <p>Vessel Capacity: {vesselSize} tons</p>
        <p>Power: 0 Power</p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const FullHangarConfig: React.FC<InternalConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [tonnage, setTonnage] = useState<number>(1);
  
  const handleConfigure = () => {
    onConfigure(system, { tonnage });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tonnage</label>
        <input
          type="number"
          min={1}
          value={tonnage}
          onChange={(e) => setTonnage(Math.max(1, Number(e.target.value)))}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div className="text-sm text-gray-600">
        <p>Cost: {(tonnage * 50000).toLocaleString()} Cr</p>
        <p>Tonnage: {tonnage} tons</p>
        <p>Power: 0 Power</p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const LaboratoryConfig: React.FC<InternalConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [tonnage, setTonnage] = useState<number>(1);
  
  const handleConfigure = () => {
    onConfigure(system, { tonnage });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tonnage</label>
        <input
          type="number"
          min={1}
          value={tonnage}
          onChange={(e) => setTonnage(Math.max(1, Number(e.target.value)))}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div className="text-sm text-gray-600">
        <p>Cost: {(tonnage * 1000000).toLocaleString()} Cr</p>
        <p>Tonnage: {tonnage} tons</p>
        <p>Power: {tonnage * 2} Power</p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const LaunchTubeConfig: React.FC<InternalConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [tonnage, setTonnage] = useState<number>(1);
  
  const handleConfigure = () => {
    onConfigure(system, { tonnage });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tonnage</label>
        <input
          type="number"
          min={1}
          value={tonnage}
          onChange={(e) => setTonnage(Math.max(1, Number(e.target.value)))}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div className="text-sm text-gray-600">
        <p>Cost: {(tonnage * 200000).toLocaleString()} Cr</p>
        <p>Tonnage: {tonnage} tons</p>
        <p>Power: 0 Power</p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const RecoveryDeckConfig: React.FC<InternalConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [tonnage, setTonnage] = useState<number>(1);
  
  const handleConfigure = () => {
    onConfigure(system, { tonnage });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tonnage</label>
        <input
          type="number"
          min={1}
          value={tonnage}
          onChange={(e) => setTonnage(Math.max(1, Number(e.target.value)))}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div className="text-sm text-gray-600">
        <p>Cost: {(tonnage * 100000).toLocaleString()} Cr</p>
        <p>Tonnage: {tonnage} tons</p>
        <p>Power: 0 Power</p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const StudioConfig: React.FC<InternalConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [tonnage, setTonnage] = useState<number>(1);
  
  const handleConfigure = () => {
    onConfigure(system, { tonnage });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tonnage</label>
        <input
          type="number"
          min={1}
          value={tonnage}
          onChange={(e) => setTonnage(Math.max(1, Number(e.target.value)))}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div className="text-sm text-gray-600">
        <p>Cost: {(tonnage * 500000).toLocaleString()} Cr</p>
        <p>Tonnage: {tonnage} tons</p>
        <p>Power: {tonnage * 1} Power</p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const TrainingFacilitiesConfig: React.FC<InternalConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [tonnage, setTonnage] = useState<number>(1);
  
  const handleConfigure = () => {
    onConfigure(system, { tonnage });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tonnage</label>
        <input
          type="number"
          min={1}
          value={tonnage}
          onChange={(e) => setTonnage(Math.max(1, Number(e.target.value)))}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div className="text-sm text-gray-600">
        <p>Cost: {(tonnage * 200000).toLocaleString()} Cr</p>
        <p>Tonnage: {tonnage} tons</p>
        <p>Power: {tonnage * 1} Power</p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const VaultConfig: React.FC<InternalConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [tonnage, setTonnage] = useState<number>(1);
  
  const handleConfigure = () => {
    onConfigure(system, { tonnage });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tonnage</label>
        <input
          type="number"
          min={1}
          value={tonnage}
          onChange={(e) => setTonnage(Math.max(1, Number(e.target.value)))}
          className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div className="text-sm text-gray-600">
        <p>Cost: {(tonnage * 1000000).toLocaleString()} Cr</p>
        <p>Tonnage: {tonnage} tons</p>
        <p>Power: 0 Power</p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};
